/**
 * Entry point. Wires pixi + sim + renderer + input + DOM UI (windows + taskbar).
 */

import { Application, Point, type FederatedPointerEvent } from 'pixi.js';
import { CONFIG } from './config';
import { createCamera, headingForFacing, headingForFocus, updateCameraSmoothed } from './render/camera';
import { Renderer } from './render/renderer';
import { computeSoundField } from './render/soundField';
import { computeVisionField } from './render/visionField';
import { advanceEntityAnimation, triggerAttackAnim, triggerHitAnim } from './sim/animation';
import { broadcastCaution, engageGuard, escalateSearch, guardBumpReaction, preprocessSounds, updateBlackoutWitness, updateEliminationStandDown, updateGuardAwareness } from './sim/awareness';
import { updateGuardBloodTracking } from './sim/bloodTrail';
import { BloodMap, bloodFromSeverity, bleedTickAmount, stepIntoTile } from './sim/blood';
import { applyDamage, canOperateWeapon, fullHeal } from './sim/body';
import { hasLineOfFire, spawnBullet, updateBullets, type Bullet, type ImpactContext } from './sim/bullet';
import { attemptRangedShot, resolveAttack, type AttackResult } from './sim/combat';
import { BODY_LAYOUT, computeReticulePosition, limbAtReticule, limbCenter } from './sim/reticule';
import { noteIncomingAttack, cycleFocus, refreshFocus, syncPlayerFov, toggleFocus, updatePlayerPerception } from './sim/focus';
import { FovMemory, updateGuardMemory } from './sim/fovMemory';
import { spawnGuard, sweepFlashlightFacing, sweepPatrolFacing, updateGuard, type Guard, type GuardState } from './sim/guard';
import { guardCombatTick, guardShouldHoldGround, updateGuardCover } from './sim/guardCombat';
import {
  consumeCycleNext,
  consumeCyclePrev,
  consumeDebugToggle,
  consumeFocusToggle,
  consumeCoverToggle,
  consumeHelpToggle,
  consumeInteract,
  consumeRelock,
  consumeSheathe,
  consumeThrow,
  consumeUniform,
  consumeMark,
  consumeNightVisionToggle,
  consumeInventoryToggle,
  consumeTurn,
  consumeVertical,
  consumeWeaponSwitch,
  createInput,
  isFireHeld,
  setKey,
} from './sim/input';
import { CORPSE_AMMO_COUNT, createKey, createUniform, findAdjacentLoot, type Item } from './sim/inventory';
import { DISGUISE_LABEL, type DisguiseKind } from './sim/disguise';
import { buildColor, buildLabel } from './build';
import { QuestLog } from './ui/questLog';
import { raiseAlarm, updateCameras } from './sim/camera';
import { scoreMission } from './sim/score';
import { canEnterVent, crawlLoudness, crawlSeconds, grilleAt, ventExit, ventNetwork } from './sim/vents';
import { stairArrival, stairDirection } from './sim/dungeon/stairs';
import { equipPockets } from './sim/pockets';
import { attemptSteal, robbedReaction, stealChance } from './sim/pickpocket';
import { PocketWindow } from './ui/pocketWindow';
import { informGuard } from './sim/janitor';
import { SteamField } from './sim/steam';
import {
  escortAtHand, escortSecured, followThroughStairs, spawnEscortee, toggleFollow,
  updateEscort, type Escortee,
} from './sim/escort';
import { MissionEndScreen } from './ui/missionEndScreen';
import {
  bindTargets, deriveQuestline, discoverBySight, missionComplete, updateQuests,
  visibleTasks, type MissionType, type Questline,
} from './sim/quest';
import { FixedLoop } from './sim/loop';
import { damageLock, reLock, regenerateLock, unlockWithKey, type Lock } from './sim/lock';
import { startPick, pickMoveCursor, pickLift, pickShimmy, pickToggleTool, pickReset, type PickSession } from './sim/lockpick';
import type { Targetable } from './sim/targetable';
import { addMuzzleLight, anyLightsOff, computeFlashlights, computeLightField, lightLevelAt, perceivedLight, restoreOneRoom, roomAt, sunlightIntensity, tickDynamicLights, toggleRoomLights, updateNightVisionGoggles } from './sim/light';
import { blindBurst, blindEntity, tickBlind } from './sim/blind';
import { doorLocked, inBounds, isBlocked, itemLocationLabel, tileAt, toggleDoor, type DoorInfo, type MapData } from './sim/map';
import { resolveTorso, resolveTorsoHold, syncVisual, updatePlayer, updateSpectator } from './sim/movement';
import { respawnPlayer, spawnPlayer } from './sim/player';
import { footstepLoudness, SoundQueue } from './sim/soundQueue';
import { buildTestMap, getJanitorSpawn } from './sim/testMap';
import { generateBuilding, type Building } from './sim/dungeon/building';
import {
  outletAtHand, serviceAtHand, tickServices, tamperService as tamperService_,
  type Service, type ServiceKind,
} from './sim/dungeon/services';
import {
  applyPropAction, channelFraction, createChannel, findPropAction, landThrownObject,
  throwLanding, updateChannel, type PropAction,
} from './sim/dungeon/propAction';
import { buildProfile, rollEstablishment, type Budget, type OperatingState } from './sim/establishment';
import { hashSeed, mulberry32 } from './sim/rng';
import { assignGuardToRoom, buildFloorPatrol, nearestWalkable, spawnFloorGuards } from './sim/patrol';
import { updateDispatch } from './sim/dispatch';
import { updateCorpseWatch } from './sim/corpseWatch';
import { callJanitor, spawnJanitor, updateJanitor, type Janitor } from './sim/janitor';
import { populateCivilians, updateCivilian, type Civilian } from './sim/civilian';
import { isCoverAnchor, isCoverTile, TileType } from './sim/tiles';
import { refillAmmo, tickWeaponCooldowns } from './sim/weapons';
import { World } from './sim/world';
import { ActionPrompts, type ActionPrompt } from './ui/actionPrompts';
import { FocusPrompt } from './ui/focusPrompt';
import { DebugConsole } from './ui/debugConsole';
import { CompassBar } from './ui/compassBar';
import { FuseboxPanel } from './ui/fuseboxPanel';
import { LockWindow } from './ui/lockWindow';
import { InventoryWindow } from './ui/inventoryWindow';
import { DamageNumbers } from './ui/damageNumbers';
import { DamageVignette } from './ui/damageVignette';
import { FocusVignette } from './ui/focusVignette';
import { HealVignette } from './ui/healVignette';
import { StanceDisplay } from './ui/stanceDisplay';
import { TutorialOverlay } from './ui/tutorialOverlay';
import { DebugOverlay } from './ui/debugOverlay';
import { MovementDevPanel } from './ui/movementDevPanel';
import { FocusPanel } from './ui/focusPanel';
import { InventoryOverlay } from './ui/inventoryOverlay';
import { EventLog } from './ui/log';
import { Minimap } from './ui/minimap';
import { SurroundView } from './ui/surroundView';
import { SidePanel } from './ui/sidePanel';
import { Taskbar } from './ui/taskbar';
import { TitleScreen, type MissionParams } from './ui/titleScreen';
import { GameWindow } from './ui/window';

const CAPTURE_KEYS = new Set([
  'KeyW', 'KeyA', 'KeyS', 'KeyD',
  'KeyC', 'KeyR', 'KeyF', 'KeyG', 'KeyV', 'KeyM', 'KeyE', 'KeyQ', 'KeyL', 'KeyX', 'KeyN', 'KeyT', 'KeyI',
  'Digit1', 'Digit2', 'Digit3', 'Digit4',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'ShiftLeft', 'ShiftRight',
  'ControlLeft', 'ControlRight',
  'Backquote', 'Space',
]);

const STATE_LADDER: readonly GuardState[] = ['unaware', 'suspicious', 'cautious', 'alert'];

function highestState(guards: readonly Guard[]): GuardState {
  let best: GuardState = 'unaware';
  for (const g of guards) {
    if (g.body.dead) continue;
    if (STATE_LADDER.indexOf(g.state) > STATE_LADDER.indexOf(best)) best = g.state;
  }
  return best;
}

/**
 * Situational action affordances available to the player right THIS tick — the
 * contextual prompt display shows these. Movement/camera keys are intentionally
 * excluded (always available → not worth prompting). Dead → only respawn.
 */
/** Is the player standing next to the fusebox (Chebyshev ≤ 1)? */
function nearFusebox(player: import('./sim/player').Player, map: import('./sim/map').MapData): boolean {
  if (!map.fusebox) return false;
  return Math.max(Math.abs(map.fusebox.x - player.x), Math.abs(map.fusebox.y - player.y)) <= 1;
}

const cheb = (ax: number, ay: number, bx: number, by: number): number =>
  Math.max(Math.abs(ax - bx), Math.abs(ay - by));

/** The door adjacent to the player (Chebyshev ≤ 1), or null. */
function nearDoor(player: import('./sim/player').Player, map: MapData): DoorInfo | null {
  for (const d of map.doors) if (cheb(player.x, player.y, d.x, d.y) <= 1) return d;
  return null;
}

/** The lootable container adjacent to the player (Chebyshev ≤ 1), or null. */
function nearContainer(
  player: import('./sim/player').Player,
  map: MapData,
): import('./sim/container').Container | null {
  for (const c of map.containers) if (cheb(player.x, player.y, c.x, c.y) <= 1) return c;
  return null;
}

/** A key in the player's dungeon loot that opens lock `lockId`, or null. */
function keyFor(player: import('./sim/player').Player, lockId: number | null): Item | null {
  if (lockId === null) return null;
  return player.dungeonLoot.find((it) => it.kind === 'key' && it.lockId === lockId) ?? null;
}

function computeActionPrompts(
  player: import('./sim/player').Player,
  guards: readonly Guard[],
  map: import('./sim/map').MapData,
  vent?: { onGrille: boolean },
): ActionPrompt[] {
  const out: ActionPrompt[] = [];
  if (player.body.dead) {
    out.push({ key: 'R', label: 'Respawn', accent: '#ff6a6a' });
    return out;
  }
  // E priority: fusebox → door → container → corpse loot.
  const pDoor = nearDoor(player, map);
  const pContainer = nearContainer(player, map);
  if (nearFusebox(player, map)) {
    out.push({ key: 'E', label: 'Fusebox', accent: '#ffd23f' });
  } else if (pDoor) {
    const dlk = pDoor.lockId !== null ? map.locks.find((l) => l.id === pDoor.lockId) : undefined;
    const locked = doorLocked(map, pDoor);
    const hasKey = keyFor(player, pDoor.lockId) !== null;
    const label = locked ? (hasKey ? 'Unlock door' : 'Locked') : (tileAt(map, pDoor.x, pDoor.y) === TileType.DoorClosed ? 'Open door' : 'Close door');
    out.push(locked && !hasKey ? { key: 'E', label, accent: '#ff8a5a' } : { key: 'E', label });
    // Any intact, unlocked lock can be pushed shut again — no key needed.
    if (!locked && dlk && !dlk.body.dead) out.push({ key: 'L', label: 'Re-lock', accent: '#8ad0ff' });
  } else if (pContainer) {
    const lk = pContainer.lockId !== null ? map.locks.find((l) => l.id === pContainer.lockId) : undefined;
    const locked = lk !== undefined && lk.locked;
    const hasKey = keyFor(player, pContainer.lockId) !== null;
    const label = locked ? (hasKey ? 'Unlock cabinet' : 'Locked') : (pContainer.looted ? 'Empty' : 'Loot cabinet');
    out.push(locked && !hasKey ? { key: 'E', label, accent: '#ff8a5a' } : { key: 'E', label });
    if (!locked && lk && !lk.body.dead) out.push({ key: 'L', label: 'Re-lock', accent: '#8ad0ff' });
  } else if (findAdjacentLoot(player.x, player.y, guards).length > 0) {
    // Loot an adjacent corpse.
    out.push({ key: 'E', label: 'Loot' });
  } else if (player.concealedAt) {
    out.push({ key: 'E', label: 'Climb out', accent: '#22e6ff' });
  } else {
    // Nothing to loot → the prop sandbox has the E slot (flip/hide/stash/take).
    const body = guards.some(
      (g) => g.body.dead && !g.stashed
        && Math.max(Math.abs(g.x - player.x), Math.abs(g.y - player.y)) <= 1,
    );
    const prop = findPropAction(map, player.x, player.y, { bodyAtHand: body });
    if (prop) {
      const label = {
        flip: 'Flip for cover', hide: 'Hide inside', stash: 'Stash body',
        take: 'Take object', topple: 'Topple the shelving', shove: 'Shove it along',
      }[prop.verb];
      const loud = prop.verb === 'flip' || prop.verb === 'stash' || prop.verb === 'topple';
      out.push(loud ? { key: 'E', label, accent: '#ffd23f' } : { key: 'E', label });
    }
  }
  // A carried object can be hurled to make noise somewhere you aren't.
  if (player.throwables > 0) out.push({ key: 'B', label: `Throw (${player.throwables})`, accent: '#8ad0ff' });
  // Standing on a duct grille: crouch to get in.
  if (vent?.onGrille) {
    out.push(player.stance === 'crouch'
      ? { key: 'E', label: 'Crawl the duct', accent: '#22e6ff' }
      : { key: 'C', label: 'Crouch to enter duct' });
  }
  // Cover: take it if a wall/half-wall/cubicle is adjacent; while in it, leave
  // it, and climb over if the cover ahead is low and the far side is walkable.
  if (!player.inCover) {
    const dirs: ReadonlyArray<readonly [number, number]> = [[0, -1], [0, 1], [-1, 0], [1, 0]];
    if (dirs.some(([dx, dy]) => isCoverAnchor(tileAt(map, player.x + dx, player.y + dy)))) {
      out.push({ key: 'X', label: 'Take cover' });
    }
  } else {
    out.push({ key: 'X', label: 'Leave cover' });
    // Hold C to rise up and peek over the cover (release tucks back down).
    out.push({ key: 'C', label: 'Peek over' });
    const coverT = tileAt(map, player.x + player.coverDx, player.y + player.coverDy);
    const farX = player.x + 2 * player.coverDx;
    const farY = player.y + 2 * player.coverDy;
    if (isCoverTile(coverT) && inBounds(map, farX, farY) && !isBlocked(map, farX, farY)) {
      out.push({ key: 'W', label: 'Climb over' });
    }
  }
  // Melee: adjacent to a live guard → takedown (if it's unaware) or attack.
  const activeW = player.weapons[player.activeWeaponIdx];
  const adjGuard = guards.find(
    (g) => !g.body.dead && Math.max(Math.abs(g.x - player.x), Math.abs(g.y - player.y)) === 1,
  );
  if (adjGuard && activeW.isMelee) {
    const takedown = adjGuard.state === 'unaware';
    out.push(takedown
      ? { key: 'Space', label: 'Takedown', accent: '#ff6a6a' }
      : { key: 'Space', label: 'Melee' });
  }
  // Fire: a focused, live target with a clear line of fire and a ranged weapon.
  const target = player.focusedTargetId !== null
    ? guards.find((g) => g.id === player.focusedTargetId && !g.body.dead)
    : null;
  if (target && !activeW.isMelee && activeW.kind !== 'heal'
    && hasLineOfFire(map, player.x, player.y, target.x, target.y)) {
    out.push({ key: 'Space', label: 'Fire' });
  }
  return out;
}

async function main(mission?: MissionParams): Promise<void> {
  const appEl = document.getElementById('app');
  const taskbarEl = document.getElementById('taskbar');
  const winRoot = document.getElementById('windows');
  const compassEl = document.getElementById('compass');
  if (!appEl || !taskbarEl || !winRoot || !compassEl) throw new Error('missing #app / #taskbar / #windows / #compass');

  const app = new Application();
  await app.init({
    background: CONFIG.render.bgColor,
    resizeTo: window,
    antialias: false,
    autoDensity: true,
    resolution: window.devicePixelRatio || 1,
  });
  appEl.appendChild(app.canvas);

  let map = buildTestMap();
  // Set the sunlight admitted by the windows from the operation's time of day
  // (the real wall-clock hour), then recompute the light field. NATURAL sources
  // (windows) take the sun level; artificial fixtures are untouched.
  {
    const now = new Date();
    const hour = now.getHours() + now.getMinutes() / 60;
    const sun = sunlightIntensity(hour);
    for (const s of map.lights) if (s.natural) s.intensity = sun;
    computeLightField(map);
  }
  const world = new World();
  const player = spawnPlayer(world, map.spawn.x, map.spawn.y);
  let guards: Guard[] = [];
  // Section-based floor plan: partition every non-spawn room into ~5×10 patrol
  // sections, spawn one guard per section plus a dedicated floor captain. The
  // captain roams + dispatches (updateDispatch); debug-spawned guards (click)
  // fold in via assignGuardToRoom, or stay legacy wanderers off-room.
  let floor = buildFloorPatrol(map, CONFIG.guard.patrol);
  guards.push(...spawnFloorGuards(world, map, floor, CONFIG.guard.patrol));
  // The janitor idles in its closet until a guard calls it in to mop blood.
  const janitorSpawn = getJanitorSpawn();
  let janitors: Janitor[] = [spawnJanitor(world, janitorSpawn.x, janitorSpawn.y)];
  // The working population of the active floor (empty on the tutorial map).
  let civilians: Civilian[] = [];
  // The janitor carries the closet key — it drops as loot on death (a quiet way
  // into the locked cabinet that skips gunfire).
  {
    const cabinet = map.containers[0];
    if (cabinet?.lockId != null) {
      const label = itemLocationLabel(map, cabinet.x, cabinet.y, 'Key');
      janitors[0]!.heldKey = createKey(cabinet.lockId, label);
    }
  }
  let sounds = new SoundQueue();
  let fov = new FovMemory(map.width, map.height);
  let bloodMap = new BloodMap(map.width, map.height);
  /** Active projectile bullets — pushed by fire paths, updated per tick,
   *  filtered when alive=false. */
  const bullets: Bullet[] = [];
  /** Last integer (x, y) of each entity so we can detect fresh tile-entries
   *  for the blood trail system. */
  const lastTile = new Map<number, { x: number; y: number }>();
  let lastAttack: AttackResult | null = null;

  const renderer = new Renderer(app);
  renderer.init();
  renderer.loadMap(map);
  const camera = createCamera();
  // Zoom: min fits the whole map into the viewport, max = 2× default.
  // Initial zoom sits midway so the user has equal headroom in / out.
  let minZoom = Math.min(window.innerWidth / (map.width * renderer.tileSize), window.innerHeight / (map.height * renderer.tileSize)) * 0.95;
  const maxZoom = 2.0;
  // `userZoom` is the player's manually-chosen zoom (mouse wheel). While in
  // focus mode the actual `camera.zoom` may auto-zoom OUT below it to fit both
  // the player and the focused enemy on screen; it returns to `userZoom` when
  // focus ends.
  let userZoom = (minZoom + maxZoom) / 2;
  camera.zoom = userZoom;
  window.addEventListener('wheel', (e) => {
    e.preventDefault();
    const dir = e.deltaY < 0 ? 1 : -1;
    const factor = 1 + dir * 0.12;
    userZoom = Math.max(minZoom, Math.min(maxZoom, userZoom * factor));
  }, { passive: false });
  const loop = new FixedLoop(CONFIG.tickHz, CONFIG.maxTicksPerFrame);
  const input = createInput();
  const taskbar = new Taskbar(taskbarEl);

  // Windows layout — tuned for a 1280×800 target; user drags to taste.
  const logWin = new GameWindow('LOG', 'log').place({ x: 12, y: 60, w: 260, h: 220 }).mount(winRoot);
  logWin.body.classList.add('scroll'); // the LOG keeps a fixed height and scrolls; other panels fit content
  logWin.root.style.display = 'none'; // LOG + DEBUG live behind the ` debug toggle
  const eventLog = new EventLog(logWin);
  eventLog.add('Chagat Corporate Complex — Floor 1', 'system');
  eventLog.add('Esc = controls · I = inventory · ` = debug.', 'info');
  eventLog.add('M marks the focused guard.', 'info');

  const miniWin = new GameWindow('MINIMAP', 'minimap').place({ x: 12, y: 300, w: 260 }).mount(winRoot);
  const minimap = new Minimap(miniWin, map, 3);

  // 7×7 surroundings render, next to the minimap — stays centered on the player
  // so you keep tactical awareness while focus locks the camera on an enemy.
  const surroundWin = new GameWindow('SURROUNDINGS', 'surround').place({ x: 280, y: 300, w: 158 }).mount(winRoot);
  const surroundView = new SurroundView(surroundWin);

  const dungeonWin = new GameWindow('AREA', 'dungeon').place({ x: 12, y: 520, w: 260 }).mount(winRoot);
  /** Settle the run: what you carried out, and how you did it. */
  const settle = (completed: boolean) => scoreMission({
    loot: player.dungeonLoot,
    completed,
    neverSeen: !everSeen,
    noKills: ![...guards, ...civilians, ...janitors].some((n) => n.body.dead)
      && escortee?.body.dead !== true,
    ...(escortee ? { clientSafe: escortee.escortState === 'following' && !escortee.body.dead } : {}),
  });

  // Which build this is, permanently on screen. A playtest note that doesn't
  // name its build can't be acted on.
  const buildBadge = document.createElement('div');
  buildBadge.className = 'build-badge';
  buildBadge.textContent = buildLabel();
  buildBadge.style.color = buildColor();
  appEl.appendChild(buildBadge);

  const missionEnd = new MissionEndScreen(appEl, {
    onRedeploy: () => location.reload(),   // the URL already holds this contract
    onTitle: () => { location.href = location.pathname; },
  });
  const pocketWin = new GameWindow('POCKETS', 'pockets').place({ x: 288, y: 300, w: 240 }).mount(winRoot);
  const pocketWindow = new PocketWindow(pocketWin);
  /** Index into the mark's pockets, kept across ticks while the window is up. */
  let pocketIdx = 0;
  /** E pressed in the pockets window — resolved in the tick. */
  let stealRequested = false;
  /** Seconds until a phoned-in report lands, and who made the call. */
  let phoneCall: { at: number; x: number; y: number } | null = null;
  const questWin = new GameWindow('CONTRACT', 'quest').place({ x: 12, y: 630, w: 260 }).mount(winRoot);
  const questLog = new QuestLog(questWin);
  questWin.root.style.display = 'none'; // no contract on the tutorial map
  dungeonWin.setContent(`<div class="dungeon-title">CHAGAT CORP — FLOOR 1</div><div class="dungeon-sub">workhall · corridor · armory</div>`);

  // ---- Multi-floor (procedural mission mode) ----
  // The per-floor state (`map`/`guards`/`floor`/`janitors`/`sounds`) is captured
  // by the tick + render closures as mutable `let`s, so `switchToFloor` swaps the
  // active floor for everyone at once. `building` is null on the tutorial map.
  let building: Building | null = null;
  // The mission's time of day — decides how many people are in the building.
  let missionOperating: OperatingState = 'in-use';
  // The job: a chain of tasks anchored to this building (sim/quest.ts).
  let questline: Questline | null = null;
  let missionWon = false;
  // The person an extraction contract is about, once the player reaches them.
  let escortee: Escortee | null = null;
  // Run-long facts the payout is settled against (sim/score.ts).
  let everSeen = false;
  const floorRuntime = new Map<number, {
    guards: Guard[];
    civilians: Civilian[];
    floor: ReturnType<typeof buildFloorPatrol>;
  }>();

  const floorLabel = (n: number): string => (n < 0 ? `B${-n}` : `${n}F`);
  const setAreaBanner = (): void => {
    const kinds = [...new Set(map.rooms.map((r) => r.kind).filter(Boolean))].slice(0, 4).join(' · ');
    dungeonWin.setContent(
      `<div class="dungeon-title">${map.title.toUpperCase()} — ${floorLabel(map.floor)}</div>` +
      `<div class="dungeon-sub">${kinds || 'floor'}</div>`,
    );
  };

  /** Swap the active floor of `building` to index `idx`, placing the player at
   *  `arrive`. Guards for a floor are spawned lazily on first visit and cached;
   *  non-active floors don't tick (Phase 1 simplification). */
  const switchToFloor = (idx: number, arrive: { x: number; y: number }): void => {
    if (!building) return;
    building.activeFloorIdx = idx;
    const F = building.floors[idx]!;
    map = F.map;
    let rt = floorRuntime.get(idx);
    if (!rt) {
      // Staffing scales with what the building can afford (BuildProfile).
      const fp = buildFloorPatrol(map, CONFIG.guard.patrol, buildProfile(building.establishment.budget).guardCoverage);
      const gs = spawnFloorGuards(world, map, fp, CONFIG.guard.patrol);
      // Everyone on this floor gets pockets, seeded so a building is repeatable.
      const prng = mulberry32(hashSeed(building.establishment.seed, idx, 0x90c));
      for (const g of gs) equipPockets(g, g.isCaptain ? 'captain' : 'guard', map, prng);
      // Staff the floor once, on first visit: the day shift fills the offices,
      // the night shift leaves one worker on overtime.
      const cs = populateCivilians(
        world, map,
        CONFIG.operating[missionOperating].civilianDensity,
        mulberry32(hashSeed(building.establishment.seed, idx, 0xc17)),
      );
      for (const c of cs) equipPockets(c, 'civilian', map, prng);
      rt = { guards: gs, civilians: cs, floor: fp };
      floorRuntime.set(idx, rt);
    }
    guards = rt.guards;
    civilians = rt.civilians;
    floor = rt.floor;
    if (questline && !escortee) {
      // The client waits where the contract says they are — spawned the first
      // time the player actually reaches that floor.
      const rescue = questline.tasks.find((t) => t.kind === 'rescue' && t.floorIdx === idx);
      if (rescue) {
        const spot = nearestWalkable(map, rescue.x, rescue.y, 6) ?? { x: rescue.x, y: rescue.y };
        escortee = spawnEscortee(world, spot.x, spot.y, idx);
        equipPockets(escortee, 'escortee', map, mulberry32(hashSeed(building.establishment.seed, idx, 0xe5c)));
      }
    }
    if (escortee) followThroughStairs(escortee, idx, arrive);
    if (questline) {
      // The mark is whoever staffs the anchor room — bindable only now that
      // this floor's people exist.
      bindTargets(
        questline, idx,
        [...rt.civilians, ...rt.guards],
        (x: number, y: number) => roomAt(map, x, y)?.id ?? null,
      );
    }
    janitors = []; // no janitor on generated floors (Phase 1)
    sounds = new SoundQueue();
    renderer.loadMap(map);
    // Reassign (not reset) so these match the new floor's dimensions — the render
    // bloodLayer/fog were just rebuilt to that size by loadMap.
    fov = new FovMemory(map.width, map.height);
    bloodMap = new BloodMap(map.width, map.height);
    steam = new SteamField(map.width, map.height);
    bullets.length = 0;
    lastTile.clear();
    lastAttack = null;
    // Teleport the player to the arrival tile (float pos is the source of truth).
    player.x = arrive.x; player.y = arrive.y;
    player.fx = arrive.x; player.fy = arrive.y;
    player.visualX = arrive.x; player.visualY = arrive.y;
    player.prevVisualX = arrive.x; player.prevVisualY = arrive.y;
    player.moveDx = 0; player.moveDy = 0; player.moveProgress = 0;
    // Re-fit the zoom to the new floor's footprint (buildings differ in size).
    minZoom = Math.min(
      window.innerWidth / (map.width * renderer.tileSize),
      window.innerHeight / (map.height * renderer.tileSize),
    ) * 0.95;
    userZoom = Math.max(minZoom, Math.min(maxZoom, (minZoom + maxZoom) / 2));
    camera.zoom = userZoom;
    setAreaBanner();
  };

  /** Take the stairs `dir` (+1 up / −1 down) from the active floor, if that floor
   *  exists. Returns whether a change happened. */
  /** Travel a floor. `dir` +1 up / −1 down. You arrive on the destination's
   *  PAIRED tile — go up and you land on its down-stair — so the tile you
   *  arrive on can never send you straight on again. */
  const useStairs = (dir: 1 | -1): boolean => {
    if (!building) return false;
    const arrival = stairArrival(building, building.activeFloorIdx, dir);
    if (!arrival) return false;
    switchToFloor(arrival.floorIdx, arrival.at);
    stairsArmed = false; // step off before the arrival tile can fire
    eventLog.add(
      `Stairs ${dir > 0 ? 'up' : 'down'} — now ${floorLabel(building.floors[arrival.floorIdx]!.floorNum)}.`,
      'system',
    );
    return true;
  };

  /** Channel a building service into its tampered state (services.ts owns the
   *  effects; this side owns the log line + the relight). */
  const tamperService = (svc: Service): void => {
    if (!building) return;
    const res = tamperService_(building, svc);
    if (!res) return;
    for (const fi of res.relitFloors) computeLightField(building.floors[fi]!.map);
    eventLog.add(res.message, 'warn');
  };

  // Channel (hold-to-operate) state for sabotaging a service — at its plant or
  // at any of its outlets. The verb itself lives in propAction.ts.
  const channel = createChannel();
  const ventChannel = createChannel();
  const stairChannel = createChannel();
  let ventCrawling = false;
  let stairTravelling = false;
  /** Cleared on arrival so the tile you land on can't fire until you step off. */
  let stairsArmed = true;
  // Steam from a burst boiler — a sight-blocker that works on everyone.
  let steam = new SteamField(map.width, map.height);
  const fog = (x: number, y: number) => steam.opaqueAt(x, y);
  let channelKind: string | null = null;
  const serviceAmbientClock = { t: 0 };
  const channelBar = document.createElement('div');
  channelBar.className = 'channel-bar';
  channelBar.innerHTML = '<div class="channel-label"></div><div class="channel-track"><div class="channel-fill"></div></div>';
  channelBar.style.display = 'none';
  appEl.appendChild(channelBar);
  const channelFill = channelBar.querySelector('.channel-fill') as HTMLElement;
  const channelLabel = channelBar.querySelector('.channel-label') as HTMLElement;

  // The compass is a FIXED bar at top-center (not a draggable window).
  const compass = new CompassBar(compassEl);
  const inventoryOverlay = new InventoryOverlay(appEl);
  const damageNumbers = new DamageNumbers(appEl);
  const damageVignette = new DamageVignette(appEl);
  const healVignette = new HealVignette(appEl);
  const focusVignette = new FocusVignette(appEl);
  const stanceDisplay = new StanceDisplay(appEl);
  const actionPrompts = new ActionPrompts(appEl);
  const focusPrompt = new FocusPrompt(appEl);

  // Cursor hover-inspect names for the non-window HUD pieces (windows are tagged
  // from their title in GameWindow). Shown by the ` debug probe.
  compassEl.dataset.inspect = 'Compass';
  taskbarEl.dataset.inspect = 'Taskbar';
  document.querySelector('.stance-display')?.setAttribute('data-inspect', 'Character');

  // Set by the `panel` dev command: keeps the fusebox panel force-open so the
  // dev can flip breakers remotely — regardless of proximity or being alive.
  // Cleared when the panel is closed or the player interacts normally (E).
  let fuseboxDevForced = false;

  // Developer command console (T while the ` debug overlay is on).
  const runConsoleCommand = (cmd: string): string => {
    const parts = cmd.toLowerCase().split(/\s+/).filter(Boolean);
    if (parts[0] === 'kill') {
      const type = parts[1] ?? '';
      let n = 0;
      const kill = (e: { body: { dead: boolean } }): void => { if (!e.body.dead) { e.body.dead = true; n++; } };
      if (type === 'guard' || type === 'guards') for (const g of guards) kill(g);
      else if (type === 'janitor' || type === 'janitors') for (const j of janitors) kill(j);
      else if (type === 'player') kill(player);
      else if (type === 'all') { for (const g of guards) kill(g); for (const j of janitors) kill(j); }
      else return `kill: unknown type "${type}" — try: guard, janitor, player, all`;
      eventLog.add(`Console: killed ${n} ${type}.`, 'system');
      return `Killed ${n} ${type}.`;
    }
    if (parts[0] === 'godmode') {
      player.godmode = !player.godmode;
      // Bleeding out afterwards would make it useless, so clear the damage too.
      if (player.godmode) player.body.bleedPerSec = 0;
      eventLog.add(`Console: godmode ${player.godmode ? 'ON' : 'off'}.`, 'system');
      return `godmode ${player.godmode ? 'ON — nothing can hurt you' : 'off'}.`;
    }
    if (parts[0] === 'ghost') {
      player.invisible = !player.invisible;
      eventLog.add(`Console: ghost ${player.invisible ? 'ON' : 'off'}.`, 'system');
      return player.invisible
        ? 'ghost ON — no guard, civilian or camera can see you. You still make noise.'
        : 'ghost off — they can see you again.';
    }
    if (parts[0] === 'spawn') {
      const type = parts[1] ?? '';
      if (type === 'janitor') {
        const live = janitors.filter((j) => !j.body.dead).length;
        if (live >= CONFIG.janitor.quota) return `Janitor quota reached (${live}/${CONFIG.janitor.quota}).`;
        const s = getJanitorSpawn();
        janitors.push(spawnJanitor(world, s.x, s.y));
        eventLog.add('Console: spawned a janitor.', 'system');
        return `Spawned a janitor (${live + 1}/${CONFIG.janitor.quota}).`;
      }
      return `spawn: unknown type "${type}" — try: janitor`;
    }
    if (parts[0] === 'panel') {
      // Reveal a floor's breaker box (fusebox) through the fog. No arg → the
      // occupied (current) floor; a trailing number → that floor. This is a
      // single-floor build, so only floor 1 exists.
      const CURRENT_FLOOR = 1;
      const arg = parts[1];
      let floor = CURRENT_FLOOR;
      if (arg !== undefined) {
        const n = Number.parseInt(arg, 10);
        if (Number.isNaN(n)) return `panel: floor must be a number (e.g. "panel" or "panel 1").`;
        floor = n;
      }
      if (floor !== CURRENT_FLOOR) return `panel: floor ${floor} isn't loaded (only floor ${CURRENT_FLOOR}).`;
      if (!map.fusebox) return `panel: floor ${floor} has no breaker box.`;
      const { x: fx, y: fy } = map.fusebox;
      // Already dev-controlling it → `panel` again closes the controls.
      if (fuseboxDevForced && fuseboxPanel.isOpen()) {
        fuseboxDevForced = false;
        fuseboxPanel.hide();
        return `Breaker box (floor ${floor}) controls closed.`;
      }
      // Mark the breaker box + a 3×3 around it as SEEN so it shows on the map +
      // minimap even from across the floor.
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const tx = fx + dx;
          const ty = fy + dy;
          if (tx < 0 || ty < 0 || tx >= map.width || ty >= map.height) continue;
          fov.seen[ty * map.width + tx] = 1;
        }
      }
      // Grant remote control: force the fusebox panel open (and keep it open,
      // regardless of proximity or player death) so the dev can flip breakers.
      fuseboxDevForced = true;
      fuseboxPanel.show(map.rooms);
      input.held.clear(); // suspend any held movement — the menu is modal now
      eventLog.add(`Console: opened floor ${floor} breaker box controls at (${fx}, ${fy}).`, 'system');
      return `Breaker box (floor ${floor}) controls open at (${fx}, ${fy}) — flip any breaker. Run "panel" again to close.`;
    }
    return `Unknown command: "${parts[0] ?? ''}" — try: kill <guard|janitor|player|all>, spawn janitor, panel [floor]`;
  };
  const debugConsole = new DebugConsole(appEl, runConsoleCommand);
  const fuseboxPanel = new FuseboxPanel(appEl, (roomId) => {
    const on = toggleRoomLights(map, roomId);
    const room = map.rooms.find((r) => r.id === roomId);
    if (room) eventLog.add(`${room.name} lights ${on ? 'ON' : 'OFF'}.`, 'system');
    if (anyLightsOff(map)) {
      // Only guards who actually end up in the dark witness the outage — that's
      // handled per-tick by updateBlackoutWitness, not broadcast to everyone here.
      eventLog.add('Power cut in a section.', 'warn');
    }
  });

  // Lockpicking minigame (sim/lockpick.ts). The LOCK window is NON-modal: the
  // world keeps running while you pick, so the arrow keys drive the lock and
  // WASD drops the pick to move/flee (see the keydown handler + the tick guard).
  // Debug-overlay visibility (` toggle). Declared here so the lock window's dev
  // controls (Randomize + budget) can gate their visibility on it.
  let perceptionVisible = false;
  const lockWin = new GameWindow('LOCK', 'lock')
    // Wide enough that the max 9 pin columns fit without scrolling.
    .place({ x: Math.round(window.innerWidth / 2 - 165), y: window.innerHeight - 340, w: 330 })
    .mount(winRoot);
  let pickSession: PickSession | null = null;
  const lockWindow = new LockWindow(lockWin, (budget) => {
    // Dev randomizer (only reachable while the ` overlay is on): rebuild the
    // current lock's pin puzzle at the chosen budget and restart the pick.
    if (!pickSession) return;
    const lock = map.locks.find((l) => l.id === pickSession!.lockId);
    if (!lock) return;
    regenerateLock(lock, budget, Math.random);
    pickSession = startPick(lock);
    lockWindow.update(pickSession, perceptionVisible);
    eventLog.add(`Dev: randomized the lock (${budget} pins).`, 'system');
  });
  const openPick = (lock: Lock): void => {
    pickSession = startPick(lock);
    lockWindow.show(pickSession, perceptionVisible);
    input.held.clear(); // stop any held movement as you settle into the pick
    eventLog.add(`Picking the lock (security ${lock.securityLevel})…`, 'stealth');
  };
  const closePick = (): void => {
    pickSession = null;
    lockWindow.hide();
  };
  const settlePickIfSolved = (): void => {
    if (!pickSession || !pickSession.solved) return;
    const lock = map.locks.find((l) => l.id === pickSession!.lockId);
    if (lock) {
      unlockWithKey(lock);
      // A little "UNLOCKED!" floats up over the lock (above every panel) as the
      // window closes — a touch longer-lived so it's not missed.
      damageNumbers.spawnText(lock.x, lock.y, 'UNLOCKED!', 'dmg-unlock', 1.8);
    }
    eventLog.add('Lock picked — it springs open!', 'stealth');
    closePick();
  };

  const sideWin = new GameWindow('SIDE PANEL', 'side').place({ x: window.innerWidth - 280, y: 60, w: 220 }).mount(winRoot);
  const sidePanel = new SidePanel(sideWin);
  const focusWin = new GameWindow('FOCUS', 'focus').place({ x: window.innerWidth - 510, y: 60, w: 220 }).mount(winRoot);
  const focusPanel = new FocusPanel(focusWin);

  const invWin = new GameWindow('INVENTORY', 'inventory').place({ x: window.innerWidth - 520, y: 320, w: 240 }).mount(winRoot);
  const inventoryWindow = new InventoryWindow(invWin);
  taskbar.onInventoryClick(() => inventoryWindow.toggle());
  taskbar.onOptionsClick(() => tutorialOverlay.toggle());

  const debugWin = new GameWindow('DEBUG', 'debug').place({ x: window.innerWidth - 280, y: 440, w: 260 }).mount(winRoot);
  const debug = new DebugOverlay(debugWin.body);
  debug.setVisible(true);
  debugWin.root.style.display = 'none'; // shown with the LOG behind the ` debug toggle

  const moveWin = new GameWindow('MOVE', 'move').place({ x: 288, y: 60, w: 190 }).mount(winRoot);
  const movePanel = new MovementDevPanel(moveWin);
  // The MOVE dev panel + the tutorial overlay are both hidden behind the " key.
  // Return to Title reloads: the game boots behind the title screen (see the
  // bootstrap at the bottom), and main() is a one-shot — a reload is the clean
  // way back to the menu (no partial teardown of the Pixi app + window listeners).
  const tutorialOverlay = new TutorialOverlay(appEl, () => location.reload());
  moveWin.root.style.display = 'none';

  // Light-level probe: while the perception/debug overlay (`) is on, a small
  // icon + readout follows the cursor and reports the light level (0–10) of the
  // hovered tile — plus the night-vision-boosted value when NV is on. Track the
  // raw mouse position; the render loop converts it to a tile.
  const lightProbe = document.createElement('div');
  lightProbe.className = 'light-probe';
  lightProbe.style.display = 'none';
  appEl.appendChild(lightProbe);
  let mouseClientX = 0;
  let mouseClientY = 0;
  window.addEventListener('pointermove', (e) => {
    mouseClientX = e.clientX;
    mouseClientY = e.clientY;
  });

  // Keyboard-LAYOUT awareness. Movement (WASD) + aim (arrows) + Space/digits/Esc
  // stay bound to the PHYSICAL position (`code`) — the gaming convention (so WASD
  // isn't scrambled on AZERTY / Turkish-Q / Dvorak). The MNEMONIC letter shortcuts
  // (focus F, cycle G/V, mark M, interact E, cover X, night-vision N, crouch C,
  // console T, respawn R) follow the LETTER: we ask the browser (Chromium's
  // Keyboard API) which physical key produces each and remap it to its canonical
  // KeyX. `canonicalCode` is applied to every key event before the game sees it.
  //
  // INVENTORY is deliberately NOT in this set: it stays on the physical `I` key
  // (one key per function). On Turkish-Q the LETTER 'i' lives at the apostrophe
  // ('Quote') while the key LABELLED I types 'ı' — binding the letter would put
  // inventory on the apostrophe key too, so we keep just the labelled I key.
  const MNEMONIC_LETTERS = new Set('fgvmexnctrl'.split(''));
  const PHYSICAL_CODES = new Set<string>([
    'KeyW', 'KeyA', 'KeyS', 'KeyD', 'Space', 'Escape', 'Enter',
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
    'Digit1', 'Digit2', 'Digit3', 'Digit4',
  ]);
  // producedCode → canonicalCode (populated from the layout for displaced letters).
  const codeRemap = new Map<string, string>();
  {
    const kb = (navigator as unknown as { keyboard?: { getLayoutMap?: () => Promise<Map<string, string>> } }).keyboard;
    kb?.getLayoutMap?.().then((layout) => {
      for (const [code, ch] of layout) {
        const letter = typeof ch === 'string' ? ch.toLowerCase() : '';
        if (!MNEMONIC_LETTERS.has(letter)) continue;
        const canonical = 'Key' + letter.toUpperCase();
        // Only remap a DIFFERENT physical key, and never a movement/aim key (that
        // protects WASD etc.) — then the shortcut falls back to its US position.
        if (code !== canonical && !PHYSICAL_CODES.has(code)) codeRemap.set(code, canonical);
      }
    }).catch(() => { /* Keyboard API unavailable — physical positions (+ buttons) still work. */ });
  }
  /** Remap the physical key that produces a mnemonic letter to its canonical KeyX
   *  on this layout; movement/aim keys and everything else pass through. */
  const canonicalCode = (code: string): string => codeRemap.get(code) ?? code;

  window.addEventListener('keydown', (e) => {
    // Layout-normalise up front so every check below is in canonical KeyX terms.
    const code = canonicalCode(e.code);
    // While the debug console is open, keystrokes belong to the input — the game
    // ignores them (Escape closes it; the input handles the rest).
    if (debugConsole.isOpen()) {
      if (code === 'Escape') debugConsole.close();
      return;
    }
    // While an in-game menu (the fusebox) is open it's MODAL and keyboard-driven
    // — the whole game is keyboard-only. Arrows move the selection cursor,
    // Space/Enter flips the highlighted breaker, E/Escape closes it. Every key is
    // captured so the player doesn't move/fire/aim behind the menu. This is the
    // template for all in-game menus.
    if (fuseboxPanel.isOpen()) {
      e.preventDefault();
      if (e.repeat) return;
      switch (code) {
        case 'ArrowUp':
        case 'ArrowLeft':
          fuseboxPanel.moveSelection(map.rooms, -1);
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          fuseboxPanel.moveSelection(map.rooms, 1);
          break;
        case 'Space':
        case 'Enter':
          fuseboxPanel.activateSelected(map.rooms);
          break;
        case 'KeyE':
        case 'Escape':
          fuseboxDevForced = false;
          fuseboxPanel.hide();
          break;
      }
      return;
    }
    // LOCK window (lockpicking). NON-modal: the world keeps running behind it, so
    // WASD DROPS the pick and falls through to normal movement (flee!), while the
    // arrows/Tab/Space drive the lock and every other key is swallowed so you
    // don't fire/aim/switch weapons mid-pick.
    if (pickSession) {
      // Tab swaps pick/rake. Handle it FIRST and unconditionally — the browser's
      // focus-traversal default (and a focused dev <select>) must never swallow
      // it, or the tool appears "stuck".
      if (code === 'Tab') {
        e.preventDefault();
        if (!e.repeat) { pickToggleTool(pickSession); lockWindow.update(pickSession, perceptionVisible); }
        return;
      }
      // The dev budget <select> owns the keys while it has focus — let its native
      // arrow/enter handling run (don't hijack them for the pick).
      if (document.activeElement instanceof HTMLSelectElement) return;
      const isMove = code === 'KeyW' || code === 'KeyA' || code === 'KeyS' || code === 'KeyD';
      if (isMove) {
        closePick(); // bail — don't return, so the movement key still registers below
      } else if (code === 'Backquote') {
        // Let the ` debug toggle through so its dev randomizer can be revealed
        // mid-pick (falls through to setKey → consumeDebugToggle in the tick).
      } else {
        e.preventDefault();
        if (!e.repeat) {
          switch (code) {
            case 'ArrowLeft': pickMoveCursor(pickSession, -1); break;
            case 'ArrowRight': pickMoveCursor(pickSession, 1); break;
            case 'ArrowUp': pickLift(pickSession); break;
            case 'ArrowDown': pickShimmy(pickSession); break;
            // With the RAKE selected, Space works the rake; with the PICK it
            // releases tension (resets all pins).
            case 'Space':
              if (pickSession.tool === 'rake') pickShimmy(pickSession);
              else pickReset(pickSession);
              break;
            case 'KeyE':
            case 'Escape': closePick(); break;
          }
          if (pickSession) { lockWindow.update(pickSession, perceptionVisible); settlePickIfSolved(); }
        }
        return;
      }
    }
    // POCKETS window: PgUp/PgDn choose, E lifts. Movement is deliberately NOT
    // swallowed — walking away from a mark mid-decision has to stay instant.
    if (pocketWindow.isOpen() && (code === 'PageUp' || code === 'PageDown' || code === 'KeyE')) {
      e.preventDefault();
      if (!e.repeat) {
        if (code === 'PageUp') pocketIdx -= 1;
        else if (code === 'PageDown') pocketIdx += 1;
        else stealRequested = true; // resolved in the tick, where the mark is known
      }
      return;
    }
    // T opens the console, but only once the ` debug overlay is on.
    if (code === 'KeyT' && perceptionVisible && !e.repeat) {
      e.preventDefault();
      input.held.clear(); // stop any held movement while typing
      debugConsole.open();
      return;
    }
    // Haunting (the death verdict dismissed to look at the level): Esc brings
    // the verdict back rather than opening the controls overlay.
    if (code === 'Escape' && missionEnd.isHaunting() && !e.repeat) {
      e.preventDefault();
      missionEnd.reopen();
      return;
    }
    if (CAPTURE_KEYS.has(code)) e.preventDefault();
    if (code === 'KeyR' && !e.repeat) {
      respawnPlayer(player, map.spawn.x, map.spawn.y);
      fov.reset();
      eventLog.add('You respawn at start.', 'system');
      return;
    }
    setKey(input, code, true);
  });
  window.addEventListener('keyup', (e) => {
    const code = canonicalCode(e.code);
    if (CAPTURE_KEYS.has(code)) e.preventDefault();
    setKey(input, code, false);
  });

  app.stage.eventMode = 'static';
  app.stage.hitArea = app.renderer.screen;
  app.stage.on('pointerdown', (e: FederatedPointerEvent) => {
    if (e.button !== 0) return;
    const worldPt = renderer.world.toLocal(e.global);
    const tx = Math.floor(worldPt.x / renderer.tileSize);
    const ty = Math.floor(worldPt.y / renderer.tileSize);
    if (isBlocked(map, tx, ty)) return;
    if (tx === player.x && ty === player.y) return;
    if (guards.some((g) => g.x === tx && g.y === ty)) return;
    const g = spawnGuard(world, tx, ty);
    // A hand-placed guard carries what any other guard would — otherwise the
    // debug tool quietly produces someone with empty pockets.
    equipPockets(g, 'guard', map, mulberry32(hashSeed(g.id, 0x90c)));
    guards.push(g);
    // Fold into the floor plan: a real room joins it (and re-partitions that
    // room for the new count); a spawn-room / off-room drop stays a wanderer.
    assignGuardToRoom(floor, map, guards, g, CONFIG.guard.patrol);
    eventLog.add(`Guard #${g.id} deployed at (${tx}, ${ty}).`, 'system');
  });

  const isOccupied = (x: number, y: number): boolean => {
    if (player.x === x && player.y === y) return true;
    for (const g of guards) if (!g.body.dead && g.x === x && g.y === y) return true;
    for (const j of janitors) if (!j.body.dead && j.x === x && j.y === y) return true;
    for (const c of civilians) if (!c.body.dead && c.x === x && c.y === y) return true;
    if (escortee && !escortee.body.dead && escortee.x === x && escortee.y === y) return true;
    return false;
  };
  const playerAt = (x: number, y: number): boolean => !player.body.dead && player.x === x && player.y === y;

  // Shove a blocking ALLY (guard or janitor) out of (x, y) into a free adjacent
  // tile, preferring the direction away from the pusher at (fromX, fromY). Lets a
  // boxed-in guard make room instead of stalling. Returns true if it cleared the
  // tile. Only shoves an ally that's settled on a tile (not mid-step).
  const PUSH_DIRS: ReadonlyArray<readonly [number, number]> = [
    [1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1],
  ];
  const pushAlly = (x: number, y: number, fromX: number, fromY: number): boolean => {
    const ally =
      guards.find((g) => !g.body.dead && g.x === x && g.y === y && g.moveProgress === 0) ??
      janitors.find((j) => !j.body.dead && j.x === x && j.y === y && j.moveProgress === 0);
    if (!ally) return false;
    const awayX = Math.sign(x - fromX);
    const awayY = Math.sign(y - fromY);
    // Try the away-from-pusher direction and its components first, then any tile.
    const ordered = [...PUSH_DIRS].sort((a, b) => score(b) - score(a));
    function score([dx, dy]: readonly [number, number]): number {
      return (dx === awayX ? 1 : 0) + (dy === awayY ? 1 : 0);
    }
    for (const [dx, dy] of ordered) {
      const nx = x + dx;
      const ny = y + dy;
      if (!inBounds(map, nx, ny) || isBlocked(map, nx, ny) || isOccupied(nx, ny)) continue;
      // Slide the ally over one tile (keep the visual smooth, then resync).
      ally.x = nx;
      ally.y = ny;
      ally.moveProgress = 0;
      ally.moveDx = 0;
      ally.moveDy = 0;
      ally.path = null;
      syncVisual(ally);
      return true;
    }
    return false;
  };

  // Live "how loud am I" meter (footsteps + gunshots the player emits). Bumped
  // to the loudest fresh player emission, then decays each frame so the taskbar
  // noise bar rises on a step/shot and fades back to silence.
  let playerNoise = 0;
  const NOISE_DECAY_PER_SEC = 16;
  // The loudest the player could ever be = the max of a running footstep and
  // any weapon's shot loudness. The noise bar is scaled against this.
  const maxNoise = Math.max(
    footstepLoudness('run', CONFIG.sound.loudness),
    ...player.weapons.map((w) => w.loudness ?? 0),
  );

  const emitSound = (x: number, y: number, loud: number, emitter: number | 'player') => {
    if (emitter === 'player') playerNoise = Math.max(playerNoise, loud);
    return sounds.emit(x, y, loud, emitter);
  };

  /** The nearest adjacent corpse that could be dragged into a prop, or null.
   *  A stashed body is already put away; the janitor counts as a body too. */
  const bodyAtHand = (): Guard | null =>
    [...guards, ...janitors].find(
      (g) => g.body.dead && !g.stashed
        && Math.max(Math.abs(g.x - player.x), Math.abs(g.y - player.y)) <= 1,
    ) ?? null;

  // Prop sandbox (Phase 3): run a prop verb the player just triggered. Map-
  // mutating verbs go through the sim module; 'hide'/'stash' move people, so
  // they're applied here. Either way this side owns the noise, the log line,
  // and the re-light (a flipped prop is a new sight/light blocker).
  const runPropAction = (action: PropAction): void => {
    if (action.verb === 'hide') {
      player.concealedAt = { x: action.x, y: action.y };
      emitSound(action.x, action.y, action.loudness, 'player'); // a soft scuffle
      eventLog.add(action.message, 'system');
      return;
    }
    if (action.verb === 'stash') {
      const body = bodyAtHand();
      if (!body) return; // dragged off / already stashed since the query
      body.stashed = true;
      emitSound(action.x, action.y, action.loudness, 'player'); // dead weight thumps
      eventLog.add(`${action.message} The evidence is out of sight.`, 'stealth');
      return;
    }
    if (action.verb === 'take' && player.throwables >= CONFIG.prop.maxThrowables) {
      eventLog.add("Your hands are full — you're carrying all you can throw.", 'system');
      return;
    }
    if (!applyPropAction(map, action, undefined, { x: player.x, y: player.y })) return;
    computeLightField(map);
    emitSound(action.x, action.y, action.loudness, 'player'); // the crash carries far
    if (action.verb === 'take') {
      player.throwables += 1;
      eventLog.add(`${action.message} B throws it. (${player.throwables})`, 'system');
    } else {
      eventLog.add(action.message, 'warn');
    }
  };

  /** B: hurl a carried object along the facing. The clatter — and so the
   *  investigation it buys — happens where the object LANDS, not here. */
  const throwObject = (): void => {
    if (player.throwables <= 0) return;
    const spot = throwLanding(map, player.x, player.y, player.facingDx, player.facingDy, CONFIG.prop.throwRange);
    if (!spot) { eventLog.add('No room to throw — something is in the way.', 'system'); return; }
    player.throwables -= 1;
    landThrownObject(map, spot.x, spot.y);
    // Straight to the sound queue, NOT through emitSound: the clatter is not
    // noise the player is making at their own tile, so the noise bar stays put.
    sounds.emit(spot.x, spot.y, CONFIG.prop.throwLoudness, 'player');
    eventLog.add(`You hurl the object — it clatters ${Math.max(Math.abs(spot.x - player.x), Math.abs(spot.y - player.y))} tiles away.`, 'stealth');
  };

  /** Any WASD key down — the "you're on the move" test shared by the verbs that
   *  a step interrupts (prop concealment, the sabotage channel). */
  const movementHeld = (): boolean =>
    input.held.has('KeyW') || input.held.has('KeyA') || input.held.has('KeyS') || input.held.has('KeyD');

  /** Step back out of a cabinet. `why` colours the log line; silent on death. */
  const leaveConcealment = (why: 'moved' | 'stepped' | 'death'): void => {
    if (!player.concealedAt) return;
    player.concealedAt = null;
    if (why !== 'death') {
      emitSound(player.x, player.y, CONFIG.prop.hideLoudness, 'player');
      eventLog.add(why === 'moved' ? 'You slip out of the cabinet.' : 'You climb out.', 'system');
    }
  };

  // Track state-change transitions to produce log lines.
  const prevGuardState = new Map<number, GuardState>();
  // One-shot guard so the "all clear" line logs once per player death, not every tick.
  let eliminationLogged = false;
  // Death → spectator transition (one-shot setup on the tick the player dies).
  let wasDead = false;
  // Full-map reveal set for spectator mode — every tile index (fog skips Void).
  // Precomputed once since the map is static; reused each dead tick.
  const fullReveal = new Set<number>();
  for (let i = 0; i < map.width * map.height; i++) fullReveal.add(i);

  const tick = () => {
    world.snapshotPrev();
    world.ticks++;
    // Stamp the current tick so every blood deposit this frame records its
    // freshness (drives blood-trail direction — see sim/bloodTrail.ts).
    bloodMap.tick = world.ticks;

    // Flash-blind: age everyone's blind timer. NV goggles worn in bright light
    // overwhelm the wearer the moment their tile turns too bright — but the goggles
    // stay ON (they're still on your face). So you're dazzled ONCE on looking into
    // the light (dizzy + whiteout); as the daze fades the world reads night-vision
    // green with the bright tiles glaring out (renderer's nvGlareAlpha). The
    // nvOverwhelmed latch stops a standing-in-the-glare strobe of re-blinds — it
    // re-arms only once you're back in tolerable light. The captain's goggles are
    // auto-managed (and DO auto-off) in updateNightVisionGoggles below.
    tickBlind(player, loop.dt);
    for (const g of guards) tickBlind(g, loop.dt);
    for (const j of janitors) tickBlind(j, loop.dt);
    if (!player.body.dead && player.nightVisionOn) {
      const overwhelming = lightLevelAt(map, player.x, player.y) >= CONFIG.light.nvBlindAt;
      if (overwhelming && !player.nvOverwhelmed) {
        blindEntity(player);
        player.nvOverwhelmed = true;
        eventLog.add('Night vision overwhelmed — you\'re blinded!', 'warn');
      } else if (!overwhelming) {
        player.nvOverwhelmed = false;
      }
    } else {
      player.nvOverwhelmed = false;
    }

    // NPCs the player can perceive / focus / shoot: guards AND the janitor. The
    // guard-AI passes still use `guards` alone; only focus + combat see `targets`.
    const targets: Guard[] = janitors.length ? [...guards, ...janitors] : guards;
    // Focus/perception also targets LOCKS (focusable + shootable). Guard-specific
    // logic (mark/engage) stays on `targets`; only focus reads `focusTargets`.
    // Focus sees every LIVE entity — guards, the janitor, civilians, the client
    // — plus locks. (`targets` stays guards+janitors: mark/engage/combat logic
    // reads that one and shouldn't start treating a worker as a combatant.)
    const live: Guard[] = [...targets, ...civilians];
    if (escortee && building && escortee.floorIdx === building.activeFloorIdx) live.push(escortee);
    const focusTargets: Targetable[] = map.locks.length ? [...live, ...map.locks] : live;

    const focusForHeading = player.focusedTargetId !== null
      ? focusTargets.find((g) => g.id === player.focusedTargetId && !g.body.dead)
      : null;

    // When the player is downed, no player function works except respawn (R,
    // handled in the keydown listener). Movement, cover/vault, focus, loot,
    // weapon-switch and fire are all gated on `!player.body.dead` below — this
    // is also what stops a corpse from vaulting over cover.
    const alive = !player.body.dead;
    if (alive) eliminationLogged = false; // re-arm the all-clear for the next death

    // Death → free-fly spectator. On the tick the player dies, drop focus/cover
    // and freeze the look direction to north-up so the observer camera is stable
    // (facing never changes again while dead — no look control). Re-armed on
    // respawn (which resets facing via respawnPlayer).
    if (!alive && !wasDead) {
      player.focusActive = false;
      player.focusedTargetId = null;
      player.inCover = false;
      player.facingDx = 0;
      player.facingDy = -1;
      // Pin the corpse to where we fell — the entity itself now roams as a ghost.
      player.deathTile = { x: player.x, y: player.y };
      eventLog.add('You are down. Free-fly: WASD to roam · R to respawn.', 'system');
      // On a contract, dying ends the run — the tutorial keeps its respawn.
      if (questline) missionEnd.show('failed', questline, `${map.title} · you didn't walk out`, settle(false));
    }
    wasDead = !alive;

    // LEGS move (WASD, camera-relative — W = forward/up-on-screen, latched at
    // press time through the rotation the renderer is actually showing); the
    // torso aims separately (below).
    // Prop concealment breaks the instant you move (E climbs out deliberately).
    if (player.concealedAt && (!alive || movementHeld())) leaveConcealment(alive ? 'moved' : 'death');
    if (alive) updatePlayer(player, input, map, loop.dt, camera.headingCurrent, emitSound);
    // Dead → drift as a free-fly spectator (through walls, no vision, no facing).
    else updateSpectator(player, input, map, loop.dt, camera.headingCurrent);

    // TORSO aim → player.facingDx/Dy, which the vision cone, weapon aim, focus,
    // and camera all read. Unfocused: the arrow keys aim the torso RELATIVE to
    // the legs (releasing them snaps the torso back to face the legs). Focused:
    // the torso points at the locked target so the '@' visually aims at the
    // enemy, and the arrows are re-purposed to cycle limbs (below). The camera
    // then rotates so the torso's look direction is screen-up.
    // Dead spectators have no look control — facing stays frozen north-up (set
    // once on death), so skip all torso aiming while downed.
    if (!alive) {
      // no-op: facing frozen
    } else if (focusForHeading) {
      // Locked on a target: aim the torso straight at it.
      player.facingDx = Math.sign(focusForHeading.x - player.x);
      player.facingDy = Math.sign(focusForHeading.y - player.y);
    } else if (player.focusActive) {
      // Primed (focus mode on, no target yet): HOLD the torso aim so you can
      // scan a direction while your legs move freely; arrows re-aim it.
      resolveTorsoHold(player, input);
    } else {
      // Unfocused: arrows aim the torso leg-relative, snapping back on release.
      resolveTorso(player, input);
    }

    const under = tileAt(map, player.x, player.y);
    if (under === TileType.AmmoPickup) refillAmmo(player.weapons);
    if (under === TileType.HealStation) fullHeal(player.body);

    if (alive) {
      syncPlayerFov(player, map, fog);
      // Peripheral vision: any live guard within 2 tiles who's emitted a sound
      // in the last ~90 ticks gets added to the player's FOV. Simulates hearing
      // footsteps at close range — you know they're right next to you even
      // without turning to look.
      const fovSet = player.fovTiles ?? new Set<number>();
      for (const g of guards) {
        if (g.body.dead) continue;
        const d = Math.max(Math.abs(g.x - player.x), Math.abs(g.y - player.y));
        if (d > 2) continue;
        const heard = sounds.events.some((e) => e.emitterId === g.id && e.age < 90);
        if (heard) fovSet.add(g.y * map.width + g.x);
      }
      player.fovTiles = fovSet;
      fov.update(fovSet);
    } else {
      // Dead spectator: 0 vision of its own (empty cone), but the whole floor is
      // revealed so you can watch the sim run anywhere.
      player.fovTiles = new Set<number>();
      fov.update(fullReveal);
    }
    // Per-guard player perception: sets hiddenByCover (crouched-behind-cover is
    // invisible) and perceivedByPlayer (in cone + heard + marked = the set you
    // can focus/aim). Renderer + focus read these flags.
    updatePlayerPerception(player, focusTargets, map, world.ticks);
    updateGuardMemory(guards, fov.currentlyVisible, map);
    refreshFocus(player, focusTargets, map);
    if (consumeFocusToggle(input) && alive) {
      toggleFocus(player, focusTargets, map);
      player.selectedLimbIdx = -1;
      player.focusPromptTimer = 0; // acted on the prompt (or dismissed it)
    }
    if (consumeCyclePrev(input) && alive) { cycleFocus(player, focusTargets, map, -1); player.selectedLimbIdx = -1; }
    if (consumeCycleNext(input) && alive) { cycleFocus(player, focusTargets, map, 1); player.selectedLimbIdx = -1; }
    if (consumeMark(input) && player.focusedTargetId !== null && alive) {
      const g = targets.find((gg) => gg.id === player.focusedTargetId);
      if (g && !g.body.dead) {
        g.marked = !g.marked;
        eventLog.add(`Guard #${g.id} ${g.marked ? 'marked' : 'unmarked'}.`, 'stealth');
      }
    }
    if (consumeRelock(input) && alive) {
      // L: put an INTACT lock back on an adjacent door/container. No key needed —
      // you just push it shut (only reopening needs the key or a pick).
      const rDoor = nearDoor(player, map);
      const rContainer = rDoor ? null : nearContainer(player, map);
      const lockId = rDoor ? rDoor.lockId : rContainer?.lockId ?? null;
      const lk = lockId !== null ? map.locks.find((l) => l.id === lockId) : undefined;
      if (lk && !lk.locked && !lk.body.dead) {
        // A door has to be shut before it can be locked.
        if (rDoor && tileAt(map, rDoor.x, rDoor.y) === TileType.Door) {
          toggleDoor(map, rDoor.x, rDoor.y);
          computeLightField(map);
        }
        reLock(lk);
        eventLog.add(`Locked the ${rDoor ? 'door' : 'cabinet'} back up.`, 'system');
      }
    }
    if (consumeInteract(input) && alive) {
      const eDoor = nearDoor(player, map);
      const eContainer = nearContainer(player, map);
      const client = escortee && building && escortAtHand(escortee, player, building.activeFloorIdx)
        ? escortee
        : null;
      if (player.concealedAt) {
        leaveConcealment('stepped'); // shut in a cabinet: E is the way out
      } else if (client) {
        const st = toggleFollow(client);
        eventLog.add(st === 'following' ? 'The client falls in behind you.' : 'The client holds position.', 'stealth');
      } else if (nearFusebox(player, map)) {
        // Standing at the fusebox → open its power controls (a modal keyboard
        // menu). Closing is handled by E/Escape in the keydown menu handler.
        // Manual use drops any dev-forced hold so proximity behaviour resumes.
        fuseboxDevForced = false;
        fuseboxPanel.show(map.rooms);
        input.held.clear(); // suspend held movement while the menu is up
      } else if (eDoor) {
        // Door: a locked door wants a matching key; otherwise E toggles it.
        const lk = eDoor.lockId !== null ? map.locks.find((l) => l.id === eDoor.lockId) : undefined;
        if (lk && lk.locked) {
          if (keyFor(player, eDoor.lockId)) { unlockWithKey(lk); eventLog.add('Unlocked the door with a key.', 'system'); }
          else openPick(lk); // no key → pick it (LOCK window)
        } else {
          const r = toggleDoor(map, eDoor.x, eDoor.y);
          if (r === 'opened' || r === 'closed') { computeLightField(map); eventLog.add(`Door ${r}.`, 'system'); }
        }
      } else if (eContainer) {
        // Container: break/key its lock first, else loot its items into dungeon loot.
        const lk = eContainer.lockId !== null ? map.locks.find((l) => l.id === eContainer.lockId) : undefined;
        if (lk && lk.locked) {
          if (keyFor(player, eContainer.lockId)) { unlockWithKey(lk); eventLog.add('Unlocked the cabinet with a key.', 'system'); }
          else openPick(lk); // no key → pick it (LOCK window)
        } else if (!eContainer.looted) {
          const n = eContainer.items.length;
          for (const it of eContainer.items) player.dungeonLoot.push(it);
          eContainer.items = [];
          eContainer.looted = true;
          eventLog.add(`Looted the cabinet · +${n} item${n === 1 ? '' : 's'}.`, 'system');
        }
      } else {
        // Loot the first adjacent corpse (guard OR janitor). Refill ranged mags
        // by CORPSE_AMMO_COUNT; a janitor corpse also drops its key into dungeon
        // loot. Mark looted so the overlay + adjacency query stop showing it.
        const nearby = findAdjacentLoot(player.x, player.y, targets);
        if (nearby.length > 0) {
          const first = nearby[0]!;
          const g = targets.find((gg) => gg.id === first.guardId);
          if (g && !g.looted) {
            g.looted = true;
            let picked = 0;
            for (const w of player.weapons) {
              if (w.isMelee || w.magazine === undefined) continue;
              const before = w.ammo ?? 0;
              const after = Math.min(w.magazine, before + CORPSE_AMMO_COUNT);
              picked += after - before;
              w.ammo = after;
            }
            // Their clothes are loot too — the uniform is how you walk out past
            // the people looking for you.
            const uniformKind: DisguiseKind = civilians.some((c) => c.id === g.id)
              ? 'worker'
              : janitors.some((j) => j.id === g.id)
                ? 'janitor'
                : 'guard';
            if (!player.dungeonLoot.some((it) => it.disguise === uniformKind)) {
              player.dungeonLoot.push(createUniform(uniformKind));
              eventLog.add(`Stripped the ${DISGUISE_LABEL[uniformKind].toLowerCase()} — U wears it.`, 'system');
            }
            // Their pockets come with the body — the same items you could have
            // lifted while they were breathing.
            if (g.pockets.length > 0) {
              for (const it of g.pockets) player.dungeonLoot.push(it);
              eventLog.add(`Emptied their pockets · +${g.pockets.length} item${g.pockets.length === 1 ? '' : 's'}.`, 'system');
              g.pockets = [];
            }
            const jani = janitors.find((j) => j.id === g.id);
            if (jani && jani.heldKey) {
              player.dungeonLoot.push(jani.heldKey);
              eventLog.add(`Took the ${jani.heldKey.label}. E unlocks its door.`, 'system');
              jani.heldKey = null;
            }
            eventLog.add(`Looted ${g.isJanitor ? 'the janitor' : `corpse #${g.id}`} · +${picked} rnd.`, 'system');
          }
        } else {
          // No door/container/lootable corpse here → try a prop verb. A body
          // already looted (or empty) beside a cabinet turns 'hide' into 'stash'.
          const action = findPropAction(map, player.x, player.y, { bodyAtHand: bodyAtHand() !== null });
          if (action) runPropAction(action);
        }
      }
    }
    // B hurls a carried object (prop verbs 'take'/'throw').
    if (consumeThrow(input) && alive) throwObject();
    // Q puts the weapon away (or draws it again). Empty hands are what let you
    // reach into someone's pocket — and read as harmless while you do.
    if (consumeSheathe(input) && alive) {
      player.sheathed = !player.sheathed;
      eventLog.add(player.sheathed ? 'You palm your weapon away.' : 'Weapon in hand.', 'system');
    }
    // U changes clothes: wear a looted uniform, or take the current one off.
    if (consumeUniform(input) && alive) {
      if (player.disguise !== null) {
        eventLog.add(`Took off the ${DISGUISE_LABEL[player.disguise].toLowerCase()}.`, 'system');
        player.disguise = null;
      } else {
        const uni = player.dungeonLoot.find((it) => it.kind === 'uniform');
        if (uni?.disguise) {
          player.disguise = uni.disguise;
          eventLog.add(`Wearing the ${DISGUISE_LABEL[uni.disguise].toLowerCase()} — walk, don't run.`, 'stealth');
        }
      }
    }
    // STAIRS: standing on one travels, after a brief hold you can walk out of.
    // Direction is the tile you're on, so there's no key to press and no
    // ambiguity about which way you're going.
    {
      const here = building && alive ? tileAt(map, player.x, player.y) : null;
      const dir = here !== null ? stairDirection(here) : null;
      if (dir === null) stairsArmed = true; // stepped off — the next one counts
      // Nothing to travel to (top floor's up-stair) → no bar, no hold.
      const canGo = dir !== null && stairArrival(building!, building!.activeFloorIdx, dir) !== null;
      const target = canGo && stairsArmed ? `${building!.activeFloorIdx}:${dir}` : null;
      const phase = updateChannel(stairChannel, {
        target,
        held: true,             // standing there IS the input
        interrupted: false,     // stepping off clears `target`, which resets it
        durationSec: CONFIG.stairs.travelSec,
        dt: loop.dt,
      });
      if (phase === 'done' && dir !== null) useStairs(dir);
      stairTravelling = phase === 'channeling';
      if (stairTravelling) {
        channelBar.style.display = '';
        channelFill.style.width = `${channelFraction(stairChannel, CONFIG.stairs.travelSec) * 100}%`;
        channelLabel.textContent = dir === 1 ? 'CLIMBING' : 'DESCENDING';
      }
    }
    // Device channel: hold E at a service — its plant room, or any outlet that
    // taps it (a riser in the stairwell, a faucet in the restroom). Interrupted
    // by moving or dying; no partial credit.
    {
      const at = building !== null && alive
        ? serviceAtHand(building.services, building.activeFloorIdx, player.x, player.y)
          ?? outletAtHand(building.services, building.activeFloorIdx, player.x, player.y)?.service ?? null
        : null;
      const target = at && at.state === 'normal' ? at : null;
      const phase = updateChannel(channel, {
        target,
        held: input.held.has('KeyE'),
        interrupted: movementHeld(),
        durationSec: CONFIG.channel.durationSec,
        dt: loop.dt,
      });
      if (phase === 'done' && target) tamperService(target);
      channelKind = phase === 'channeling' && target ? target.kind : null;
      if (channelKind) {
        channelBar.style.display = '';
        channelFill.style.width = `${channelFraction(channel, CONFIG.channel.durationSec) * 100}%`;
        channelLabel.textContent = `SABOTAGING ${channelKind.toUpperCase()}`;
      } else if (channelBar.style.display !== 'none') {
        channelBar.style.display = 'none';
      }
    }
    ventCrawling = false;
    stairTravelling = false;
    // Air-vent crawl: crouch on a grille and hold E. Same channel verb as
    // sabotage; the noise lands at BOTH ends, so someone hears you get in and
    // someone hears you get out.
    if (building && alive) {
      const net = ventNetwork(building.services, building.activeFloorIdx);
      const grille = canEnterVent(net, player.x, player.y, player.stance)
        ? grilleAt(net, player.x, player.y)
        : null;
      const phase = updateChannel(ventChannel, {
        target: grille,
        held: input.held.has('KeyE'),
        interrupted: movementHeld(),
        durationSec: net ? crawlSeconds(net) : 1,
        dt: loop.dt,
      });
      if (phase === 'done' && grille && net) {
        const exit = ventExit(net, grille);
        if (exit) {
          sounds.emit(grille.x, grille.y, crawlLoudness(net), 'player');
          player.x = exit.x; player.y = exit.y;
          player.fx = exit.x; player.fy = exit.y;
          player.visualX = exit.x; player.visualY = exit.y;
          player.prevVisualX = exit.x; player.prevVisualY = exit.y;
          player.moveDx = 0; player.moveDy = 0; player.moveProgress = 0;
          emitSound(exit.x, exit.y, crawlLoudness(net), 'player');
          eventLog.add(net.fansOff
            ? 'You crawl the dead duct and slip out elsewhere.'
            : 'You crawl the duct — the fans are loud, and so are you.', 'stealth');
        }
      }
      ventCrawling = phase === 'channeling' && net !== null;
      if (ventCrawling && net) {
        channelBar.style.display = '';
        channelFill.style.width = `${channelFraction(ventChannel, crawlSeconds(net)) * 100}%`;
        channelLabel.textContent = 'CRAWLING THE DUCT';
      }
    }

    // A sabotaged boiler vents: steam pours from the plant and every riser tap,
    // fills the space, and blinds guards, cameras and the player alike.
    if (building) {
      const boiler = building.services.find((sv) => sv.kind === 'boiler');
      if (boiler?.state === 'tampered') {
        const amount = CONFIG.steam.emitPerSec * loop.dt;
        if (boiler.floorIdx === building.activeFloorIdx) steam.emit(boiler.plant.x, boiler.plant.y, amount);
        for (const o of boiler.outlets) {
          if (o.floorIdx === building.activeFloorIdx) steam.emit(o.x, o.y, amount);
        }
      }
      if (steam.active) steam.spread(map, loop.dt);
    }

    // Sustained service effects: a tampered water main runs out of every tap and
    // a tripped alarm keeps ringing — noise the guards keep hearing.
    if (building) {
      tickServices(building.services, building.activeFloorIdx, loop.dt, (x, y, loud) => {
        sounds.emit(x, y, loud, 'player');
      }, serviceAmbientClock);
    }
    // Close the fusebox if you walk away from it — unless the dev forced it open
    // via the `panel` command (remote control, holds regardless of proximity).
    if (fuseboxPanel.isOpen() && !fuseboxDevForced && !nearFusebox(player, map)) fuseboxPanel.hide();
    // Abandon a pick if the lock is gone/opened, you died, or you're no longer
    // beside it (e.g. shoved by a guard) — the world runs live while you pick.
    if (pickSession) {
      const lk = map.locks.find((l) => l.id === pickSession!.lockId);
      const beside = lk !== undefined &&
        Math.max(Math.abs(player.x - lk.x), Math.abs(player.y - lk.y)) <= 1;
      if (!lk || !lk.locked || lk.body.dead || player.body.dead || !beside) closePick();
    }
    // Take / leave cover (X). Entering snaps the player to the current tile and
    // records the cardinal normal toward the adjacent wall/half wall/cubicle.
    if (consumeCoverToggle(input) && alive) {
      if (player.inCover) {
        player.inCover = false;
      } else {
        const dirs: ReadonlyArray<readonly [number, number]> = [[0, -1], [0, 1], [-1, 0], [1, 0]];
        for (const [dx, dy] of dirs) {
          if (isCoverAnchor(tileAt(map, player.x + dx, player.y + dy))) {
            player.inCover = true;
            player.coverDx = dx;
            player.coverDy = dy;
            player.fx = player.x;
            player.fy = player.y;
            // Tuck immediately (crouched, hidden). Without this the entering
            // tick keeps the pre-cover 'walk' stance — which the peek→focus
            // check below would misread as a peek and spuriously arm focus.
            player.stance = 'crouch';
            // Face the direction the cover is hiding you from (the threat side,
            // beyond the wall). Set the torso aim AND the leg reference frame so
            // the view orients that way immediately and holds it while tucked.
            player.facingDx = dx;
            player.facingDy = dy;
            player.legDx = dx;
            player.legDy = dy;
            eventLog.add('Took cover.', 'system');
            break;
          }
        }
      }
    }

    // Peeking out of cover (over the top via C/aim/fire, OR leaning around the
    // side) automatically enters focus mode, locking the closest eligible target
    // (or priming if none yet). `peekingOut` covers both peek styles — a
    // crouched side-peek arms focus without standing up. The persistent focus
    // mode + perception then handle lock/re-prime as you peek and tuck. F drops it.
    if (player.inCover && player.peekingOut && !player.focusActive && alive) {
      toggleFocus(player, focusTargets, map);
      player.selectedLimbIdx = -1;
    }

    // Esc toggles the controls/options overlay (the "Esc menu" — Resume + Return
    // to Title live there; the OPT taskbar button opens the same menu).
    if (consumeHelpToggle(input)) tutorialOverlay.toggle();

    // N toggles night vision — only if the device is equipped.
    if (consumeNightVisionToggle(input)) {
      if (player.hasNightVision) {
        player.nightVisionOn = !player.nightVisionOn;
        eventLog.add(`Night vision ${player.nightVisionOn ? 'ON' : 'OFF'}.`, 'system');
      } else {
        eventLog.add('No night-vision device equipped.', 'system');
      }
    }

    // I toggles the inventory window (weapons + dungeon loot).
    if (consumeInventoryToggle(input)) inventoryWindow.toggle();

    const sw = consumeWeaponSwitch(input);
    if (sw >= 1 && sw <= 4 && alive) player.activeWeaponIdx = (sw - 1) as 0 | 1 | 2 | 3;

    tickWeaponCooldowns(player.weapons, loop.dt);
    // Fade out the directional "F Focus!" prompt after a hit.
    if (player.focusPromptTimer > 0) player.focusPromptTimer = Math.max(0, player.focusPromptTimer - loop.dt);
    // Reticule drift ticks while focused so what the accuracy panel shows
    // is exactly what the shot resolves as.
    if (player.focusedTargetId !== null) player.reticuleDriftT += loop.dt;
    // Arrow-key routing:
    //  - Focused: consume arrow PRESSES to cycle the limb selection.
    //  - Unfocused: arrows already drained above (they rotated the
    //    camera), so these consumes are no-ops in that case.
    const turn = consumeTurn(input);
    const vertical = consumeVertical(input);
    if (player.focusedTargetId !== null) {
      const dirStep = turn !== 0 ? turn : vertical;
      if (dirStep !== 0) {
        const cur = player.selectedLimbIdx < 0 ? -1 : player.selectedLimbIdx;
        const n = BODY_LAYOUT.length;
        let next: number;
        if (cur < 0) {
          next = dirStep < 0 ? n - 1 : 0;
        } else {
          next = (cur + dirStep + n) % n;
        }
        player.selectedLimbIdx = next;
        const label = BODY_LAYOUT[next]!.part;
        eventLog.add(`Target: ${label}`, 'stealth');
      }
    }
    if (isFireHeld(input) && !player.body.dead) {
      const w = player.weapons[player.activeWeaponIdx];
      const target: Targetable | undefined = player.focusedTargetId !== null
        ? focusTargets.find((g) => g.id === player.focusedTargetId && !g.body.dead)
        : undefined;
      // A focused target is either a LOCK (flat HP, heavy-blunt/shootable) or a
      // guard/janitor (the humanoid combat path). Resolve which.
      const lockTarget = target ? map.locks.find((l) => l.id === target.id) : undefined;
      const guardTarget: Guard | null =
        target && !lockTarget ? (targets.find((t) => t.id === target.id) ?? null) : null;

      // Arm crippling gates ATTACK weapons (heal still works so you can patch
      // up): both arms gone → can't attack; one arm → no two-handed weapon.
      // Throttled to the weapon's fire interval so it doesn't spam the log.
      if (w.kind !== 'heal' && !canOperateWeapon(player.body, w.twoHanded ?? false)) {
        if (w.cooldown <= 0) {
          w.cooldown = w.fireIntervalSec;
          eventLog.add(`Can't use ${w.name} — arm crippled.`, 'warn');
        }
      } else if (w.kind === 'heal') {
        // Medkit — "fire" applies a full-body patch instead of damaging.
        if (w.cooldown <= 0 && (w.ammo ?? 0) > 0 && !player.body.dead) {
          w.cooldown = w.fireIntervalSec;
          if (w.ammo !== undefined) w.ammo -= 1;
          const HEAL_PER_LIMB = 30;
          let healed = 0;
          const parts = ['head', 'torso', 'leftArm', 'rightArm', 'leftLeg', 'rightLeg'] as const;
          for (const part of parts) {
            const before = player.body.hp[part];
            player.body.hp[part] = Math.min(player.body.hpMax[part], before + HEAL_PER_LIMB);
            player.body.crippled[part] = false;
            healed += player.body.hp[part] - before;
          }
          player.body.bleedPerSec = 0;
          if (healed > 0) {
            damageNumbers.spawn(player.x, player.y, -healed, false);
            eventLog.add(`Medkit +${healed} HP.`, 'system');
            healVignette.pulse(0.85);
          }
        }
      } else if (lockTarget && w.isMelee) {
        // Melee a LOCK: only a heavy-blunt weapon can smash it, and only adjacent.
        const adj = Math.max(Math.abs(target!.x - player.x), Math.abs(target!.y - player.y)) <= 1;
        if (!w.heavyBlunt) {
          if (w.cooldown <= 0) {
            w.cooldown = w.fireIntervalSec;
            eventLog.add(`The ${w.name} can't break a lock — need a heavy blunt weapon.`, 'combat');
          }
        } else if (w.cooldown <= 0 && adj) {
          w.cooldown = w.fireIntervalSec;
          emitSound(player.x, player.y, w.loudness, 'player');
          triggerAttackAnim(player, target!.x - player.x, target!.y - player.y);
          const destroyed = damageLock(lockTarget, w.damage);
          damageNumbers.spawn(target!.x, target!.y, w.damage, destroyed);
          eventLog.add(destroyed ? 'Lock smashed off.' : `${w.name} → lock (${lockTarget.hp}/${lockTarget.hpMax}).`, 'combat');
        }
      } else if (w.isMelee) {
        // Melee stays hitscan — knife takedown + adjacent-only reach.
        const attackTarget = guardTarget
          ? { x: guardTarget.x, y: guardTarget.y, body: guardTarget.body, state: guardTarget.state }
          : null;
        const result = resolveAttack(player, attackTarget ?? null, w);
        if (result.fired) {
          emitSound(player.x, player.y, result.loudness, 'player');
          lastAttack = result;
          if (guardTarget) {
            triggerAttackAnim(player, guardTarget.x - player.x, guardTarget.y - player.y);
            if (result.hit) {
              triggerHitAnim(guardTarget);
              bloodMap.add(guardTarget.x, guardTarget.y, bloodFromSeverity(result.severity));
              damageNumbers.spawn(guardTarget.x, guardTarget.y, result.damage, result.severity === 'kill');
            }
            // A non-lethal melee hit (or a missed swing they noticed) → engage.
            if (result.hit && !result.killed && !guardTarget.body.dead) engageGuard(guardTarget, player);
            if (result.killed) {
              eventLog.add(`${w.name} → Guard #${guardTarget.id} down.`, 'combat');
            } else if (result.hit) {
              eventLog.add(`${w.name} hit ${result.part} on Guard #${guardTarget.id} (${result.severity}).`, 'combat');
            } else if (result.reason === 'missed') {
              eventLog.add(`${w.name} missed Guard #${guardTarget.id}.`, 'combat');
            }
          }
        }
      } else if (target && hasLineOfFire(map, player.x, player.y, target.x, target.y)) {
        // Ranged: you can shoot anything you can focus on as long as you have a
        // clear line of fire (no hard weapon-range cap — accuracy still falls
        // off with distance). Reticule (or manual limb pick) → bullet plan;
        // damage applies when the bullet impacts.
        const shot = attemptRangedShot(player, target, w, { ignoreRange: true });
        if (shot) {
          const distFactor = Math.max(0, Math.min(1, shot.distance / Math.max(1, w.rangeTiles)));
          const ret = player.selectedLimbIdx >= 0
            ? limbCenter(player.selectedLimbIdx)
            : computeReticulePosition(player.reticuleDriftT, distFactor);
          const limb = player.selectedLimbIdx >= 0
            ? BODY_LAYOUT[player.selectedLimbIdx]!.part
            : limbAtReticule(ret);
          const b = spawnBullet({
            fromX: player.x + 0.5,
            fromY: player.y + 0.5,
            targetX: target.x + 0.5 + ret.x,
            targetY: target.y + 0.5 + ret.y,
            sourceId: player.id,
            plan: { targetId: target.id, limb, damage: w.damage },
          });
          bullets.push(b);
          emitSound(player.x, player.y, shot.loudness, 'player');
          if (w.muzzleFlash) {
            addMuzzleLight(map, player.x, player.y); // silenced → none
            // A muzzle flash blinds anyone right beside the muzzle (not you).
            blindBurst([...guards, ...janitors], player.x, player.y, CONFIG.blind.muzzleRadius, player.id);
          }
          triggerAttackAnim(player, target.x - player.x, target.y - player.y, w.muzzleFlash);
        }
      }
    }

    const fresh = [];
    for (const e of sounds.fresh()) fresh.push(e);
    const processed = preprocessSounds(fresh, map);
    // Player hearing pass: any fresh sound whose flood reaches the
    // player's tile makes its emitter audible for fadeAgeTicks. The
    // renderer reveals audible entities regardless of FOV.
    const playerTileIdx = player.y * map.width + player.x;
    for (const s of processed) {
      if (typeof s.event.emitterId !== 'number') continue;
      const rec = s.residual.get(playerTileIdx);
      if (rec !== undefined && rec > 0) {
        player.hearingUntil.set(s.event.emitterId, world.ticks + CONFIG.sound.fadeAgeTicks);
      }
    }
    // Prune expired hearing entries.
    for (const [id, until] of player.hearingUntil) {
      if (world.ticks > until) player.hearingUntil.delete(id);
    }
    // NV goggles (the captain): switch on in the dark, and flash-blind + off if
    // the tile turns bright (e.g. the player restores power). Before awareness so
    // a freshly-blinded guard is already blind for this tick's perception.
    updateNightVisionGoggles(guards, map);
    // Guard flashlights: non-NV guards in the dark light a forward cone. Compute
    // before awareness so a beam lighting the player counts toward detection.
    computeFlashlights(guards, map);
    // Pass 1: update every guard's own awareness.
    for (const g of guards) {
      if (g.body.dead) continue;
      updateGuardAwareness(g, player, processed, map, loop.dt, fog);
    }
    // Elimination stand-down: with the player dead, a guard that confirms the
    // corpse calls the all-clear and the section returns to normal patrol.
    const confirmer = updateEliminationStandDown(guards, player, map);
    if (confirmer && !eliminationLogged) {
      eventLog.add(`Guard #${confirmer.id}: target eliminated — all clear.`, 'stealth');
      // Threat dealt with → send the janitor to mop up the mess.
      if (callJanitor(janitors)) eventLog.add('The janitor is called in to clean up.', 'system');
      eliminationLogged = true;
    }
    // Bodies are evidence: a guard that lays eyes on an un-stashed corpse goes
    // cautious, marks the spot and radios it in (once per body per guard).
    for (const s of updateCorpseWatch(guards, map)) {
      eventLog.add(`Guard #${s.guard.id} finds Guard #${s.bodyId}'s body!`, 'warn');
      if (callJanitor(janitors)) eventLog.add('The janitor is called in to clean up.', 'system');
    }
    // Pass 2: broadcast cautious+ transitions to nearby guards, then log
    // state changes. Broadcasts happen after all self-updates so decay
    // doesn't fight the propagated bump on the same tick.
    for (const g of guards) {
      if (g.body.dead) continue;
      const prev = prevGuardState.get(g.id) ?? 'unaware';
      const enteredCautiousOrHigher =
        (g.state === 'cautious' || g.state === 'alert') &&
        (prev === 'unaware' || prev === 'suspicious');
      // Comms sabotage suppresses the radio net — a spooked guard can't call for
      // backup, so caution doesn't spread.
      if (enteredCautiousOrHigher && !(building?.commsDown)) {
        broadcastCaution(g, guards, CONFIG.awareness.broadcastRadius);
      }
    }
    // Pass 3: log transitions. Runs after broadcasts so recipients' new
    // state is captured in the log too.
    for (const g of guards) {
      if (g.body.dead) continue;
      const prev = prevGuardState.get(g.id) ?? 'unaware';
      if (prev !== g.state) {
        prevGuardState.set(g.id, g.state);
        if (g.state !== 'unaware') {
          eventLog.add(`Guard #${g.id}: ${prev} → ${g.state}`, g.state === 'alert' ? 'warn' : 'stealth');
        }
      }
    }
    sounds.markAllConsumed();

    // Blackout witnessing: guards that find themselves in the dark raise the
    // alarm + one heads to restore power (only if they actually see the dark).
    updateBlackoutWitness(guards, map);

    // Coverage dispatch: the captain re-balances guards across rooms over the
    // walkie-talkie (even out the shortage), retires surplus to the green room,
    // recalls reserves to an active threat, and roams. Runs before movement.
    const disp = updateDispatch(guards, map, floor, CONFIG.guard);
    if (disp.recalled > 0) eventLog.add(`Captain recalls ${disp.recalled} reserve${disp.recalled > 1 ? 's' : ''} to the threat.`, 'warn');
    else if (disp.rested > 0) eventLog.add(`Captain sends ${disp.rested} guard${disp.rested > 1 ? 's' : ''} to rest in the green room.`, 'system');
    else if (disp.reassigned > 0) eventLog.add(`Captain reassigns ${disp.reassigned} guard${disp.reassigned > 1 ? 's' : ''} to balance the floor.`, 'system');

    // Blood-trail tracking: an unaware/suspicious guard that sees blood follows
    // it to the bleeder — engaging you or patching up a wounded guard. Runs
    // after awareness/broadcast (so real sightings + duties win) and before the
    // movement pass (updateGuard walks toward bloodTrack.target).
    for (const g of guards) {
      if (g.body.dead) continue;
      const ev = updateGuardBloodTracking(g, player, guards, bloodMap, map);
      if (!ev) continue;
      switch (ev.kind) {
        case 'notice':
          eventLog.add(`Guard #${g.id} spots blood — tracking.`, 'stealth');
          break;
        case 'foundPlayer':
          eventLog.add(`Guard #${g.id} followed the blood to you!`, 'warn');
          break;
        case 'foundBody': {
          const who = ev.bodyId === player.id ? 'your corpse' : `Guard #${ev.bodyId}'s body`;
          eventLog.add(`Guard #${g.id} finds ${who} — the blood's explained.`, 'warn');
          if (callJanitor(janitors)) eventLog.add('The janitor is called in to clean up.', 'system');
          break;
        }
        case 'healed':
          eventLog.add(`Guard #${g.id} patched up Guard #${ev.patientId} (+${ev.healed} HP).`, 'system');
          // A trail that ended at a casualty (not the player) is dealt with →
          // call the janitor to mop the blood the guard just followed.
          if (callJanitor(janitors)) eventLog.add('The janitor is called in to clean up.', 'system');
          break;
        case 'lost':
          eventLog.add(`Guard #${g.id} loses the trail.`, 'stealth');
          if (callJanitor(janitors)) eventLog.add('The janitor is called in to clean up.', 'system');
          break;
      }
    }

    // Sniper-Elite search escalation: a guard that revisited its "seen" ghost and
    // found nothing (no clue, no blood) either adopts a peer's fresher sighting or
    // goes alert + sweeps the floor.
    for (const g of guards) {
      const esc = escalateSearch(g, guards);
      if (esc === 'adopted') eventLog.add(`Guard #${g.id} gets a fresh sighting from a mate.`, 'stealth');
      else if (esc === 'alerted') eventLog.add(`Guard #${g.id} finds nothing — sweeping the floor!`, 'warn');
    }

    // Guard combat pass — alert guards with LOS + in-range spawn a bullet
    // toward the player. Damage is applied at bullet impact (below), not
    // here, so the muzzle flash + sound emit at fire time but the hit
    // + blood + log wait for the bullet to arrive.
    for (const g of guards) {
      if (g.body.dead) continue;
      // Manage stance/cover (duck↔peek behind cover, face the player) BEFORE
      // firing — a ducked guard's blocked cone means guardCombatTick can't
      // get LOS, so it only shoots while peeking.
      updateGuardCover(g, player, map, loop.dt);
      const shot = guardCombatTick(g, player, map, loop.dt);
      if (shot) {
        bullets.push(shot.bullet);
        sounds.emit(g.x, g.y, shot.loudness, g.id);
        if (g.weapon.muzzleFlash) {
          addMuzzleLight(map, g.x, g.y); // silenced guards → none
          // Its muzzle flash blinds anyone adjacent — player or ally (not the shooter).
          blindBurst([player, ...guards, ...janitors], g.x, g.y, CONFIG.blind.muzzleRadius, g.id);
        }
        triggerAttackAnim(g, player.x - g.x, player.y - g.y, g.weapon.muzzleFlash);
      }
    }

    for (const g of guards) {
      const hold = guardShouldHoldGround(g, player, map);
      updateGuard(g, map, loop.dt, isOccupied, emitSound, { holdGround: hold, playerAt, push: pushAlly });
      // Patrolling in the dark with a torch → sweep the beam/cone side-to-side
      // while walking, so it scans a wider arc as it moves carefully.
      if (!g.body.dead && g.flashlightOn && g.state === 'unaware' &&
          (g.moveProgress > 0 || g.moveDx !== 0 || g.moveDy !== 0)) {
        sweepFlashlightFacing(g, loop.dt);
      } else if (
        // Active patrol sweep: an unaware guard actively working its region (or
        // the captain making his rounds) oscillates its cone across the area
        // instead of only staring where it walks. Skipped during the deliberate
        // arrival scan (which drives facing itself) and any special duty.
        !g.body.dead && g.state === 'unaware' && !g.scanning && !g.resting &&
        !g.restorePower && g.bloodTrack === null &&
        (g.isCaptain || g.secX0 !== null || g.patrol !== null)
      ) {
        sweepPatrolFacing(g, loop.dt);
      }
      // Bumped into the player (they blocked the guard's step) → the guard
      // realises someone's there and engages if it can see they're an intruder.
      if (g.bumpedPlayer) {
        const wasAlert = g.state === 'alert';
        guardBumpReaction(g, player, map);
        if (g.state === 'alert' && !wasAlert) eventLog.add(`Guard #${g.id} bumped into you!`, 'warn');
      }
      // A guard on power-restore duty that reaches the fusebox works the panel,
      // flipping ONE breaker every fuseFlipSec (staged, not instant) until the
      // whole floor is back on. It must be stood adjacent (d<=1) the whole time.
      if (g.restorePower && map.fusebox && !g.body.dead) {
        const d = Math.max(Math.abs(g.x - map.fusebox.x), Math.abs(g.y - map.fusebox.y));
        if (d <= 1) {
          g.fuseTimer -= loop.dt;
          if (g.fuseTimer <= 0) {
            const room = restoreOneRoom(map);
            if (room) {
              eventLog.add(`Guard #${g.id} flips a breaker — ${room.name} lit.`, 'warn');
              g.fuseTimer = CONFIG.light.fuseFlipSec;
            }
            if (!anyLightsOff(map)) g.restorePower = false; // all breakers back on
          }
        } else {
          g.fuseTimer = CONFIG.light.fuseFlipSec; // arriving → first flip after a beat
        }
      }
    }

    // Janitor pass — warn a guard about an intruder, mop blood when called,
    // else idle in the closet.
    for (const j of janitors) {
      const ev = updateJanitor(j, map, bloodMap, guards, player, isOccupied, loop.dt);
      if (ev === 'done') eventLog.add('The janitor finishes cleaning up.', 'system');
      else if (ev === 'sawIntruder') eventLog.add('The janitor spotted you — running to warn a guard!', 'warn');
      else if (ev === 'informed') eventLog.add('The janitor tipped off a guard about the intruder.', 'warn');
    }

    // Camera pass — wall-mounted eyes sweep their arc; a full look at you trips
    // the alarm and puts the whole floor onto the sighting at once.
    if (alive) {
      for (const trip of updateCameras(map.cameras, map, player, loop.dt, fog)) {
        raiseAlarm(guards, trip.x, trip.y);
        everSeen = true;
        eventLog.add('A camera has you — the floor is alerted!', 'warn');
      }
    }

    // POCKETS: an empty hand, a locked-on person, and close enough to reach.
    // Anything else and the window drops — including simply walking away.
    const markNow: Guard | null = (() => {
      if (!alive || !player.sheathed || player.focusedTargetId === null) return null;
      const people: Guard[] = [...guards, ...janitors, ...civilians];
      if (escortee && building && escortee.floorIdx === building.activeFloorIdx) people.push(escortee);
      const t = people.find((g) => g.id === player.focusedTargetId);
      if (!t || t.body.dead) return null;
      return Math.max(Math.abs(t.x - player.x), Math.abs(t.y - player.y)) <= 1 ? t : null;
    })();
    if (markNow) {
      if (pocketIdx >= markNow.pockets.length) pocketIdx = 0;
      pocketWindow.show(
        markNow.isJanitor ? 'Their pockets' : `Guard #${markNow.id}`,
        markNow.pockets.map((item) => ({ item, chance: stealChance(player, markNow, item) })),
        pocketIdx,
      );
    } else {
      pocketWindow.hide();
      pocketIdx = 0;
    }

    if (markNow && stealRequested) {
      stealRequested = false;
      const item = markNow.pockets[((pocketIdx % markNow.pockets.length) + markNow.pockets.length) % markNow.pockets.length];
      if (item) {
        const outcome = attemptSteal(player, markNow, item);
        if (outcome === 'taken') {
          eventLog.add(`Lifted the ${item.label.toLowerCase()}.`, 'stealth');
        } else if (outcome === 'failed') {
          eventLog.add('Your hand comes back empty — they never noticed.', 'system');
        } else {
          // Felt it. What they can DO about it is what's left in their pockets.
          const react = robbedReaction(markNow);
          emitSound(markNow.x, markNow.y, CONFIG.pickpocket.fumbleLoudness, 'player');
          engageGuard(markNow, player);
          everSeen = true;
          if (react.kind === 'radio' && !(building?.commsDown)) {
            broadcastCaution(markNow, guards, CONFIG.awareness.broadcastRadius);
            eventLog.add('Caught — and they call it straight in on the radio!', 'warn');
          } else if (react.kind === 'phone') {
            phoneCall = { at: CONFIG.pickpocket.phoneCallSec, x: player.x, y: player.y };
            eventLog.add('Caught — they back off, phone already out.', 'warn');
          } else {
            // No comms left: they have to go and find somebody, like the janitor.
            eventLog.add('Caught — but with nothing to call on, they run for help.', 'warn');
          }
        }
      }
    }

    // A phoned-in report lands after a delay — the price of leaving a worker
    // their phone.
    if (phoneCall && building) {
      phoneCall.at -= loop.dt;
      if (phoneCall.at <= 0) {
        for (const g of guards) informGuard(g, phoneCall.x, phoneCall.y);
        eventLog.add('The call gets through — security has your last position.', 'warn');
        phoneCall = null;
      }
    }

    // Escort pass — the client follows, mirrors your stance, and takes the
    // stairs with you. Losing them is losing the contract.
    if (escortee && building) {
      const ev = updateEscort(escortee, map, player, building.activeFloorIdx, isOccupied, loop.dt);
      if (ev?.kind === 'downed') eventLog.add('The client is down — there is nobody left to extract.', 'warn');
    }

    // Quest pass: discovery first (what you can see reveals the hidden job),
    // then completion, which is read entirely off state the other systems keep.
    if (questline && building && alive) {
      const seen = (x: number, y: number) =>
        player.fovTiles?.has(y * map.width + x) === true;
      for (const t of discoverBySight(questline, building.activeFloorIdx, seen)) {
        eventLog.add(`New lead: ${t.title}.`, 'stealth');
      }
      const npcs = [...guards, ...civilians, ...janitors];
      const finished = updateQuests(questline, {
        floorIdx: building.activeFloorIdx,
        isDead: (id) => npcs.find((n) => n.id === id)?.body.dead === true,
        isLooted: (id) => map.containers.find((c) => c.id === id)?.looted === true,
        isTampered: (kind) => building!.services.find((sv) => sv.kind === kind)?.state === 'tampered',
        isVisible: seen,
        playerX: player.x,
        playerY: player.y,
        ...(escortee
          ? {
              escortFollowing: escortee.escortState === 'following',
              escortSecured: escortSecured(escortee, player, building.activeFloorIdx),
            }
          : {}),
      });
      for (const t of finished) eventLog.add(`Objective complete: ${t.title}.`, 'stealth');
      if (finished.length > 0 && !missionWon && missionComplete(questline)) {
        missionWon = true;
        eventLog.add('MISSION COMPLETE — you are clear of the building.', 'system');
        missionEnd.show('complete', questline, `${map.title} · contract fulfilled`, settle(true));
      }
    }

    // Civilian pass — the building's working population walking its routines
    // (desk → coffee → meeting → desk). Their traffic is the thing the player
    // reads and exploits, so it runs every tick like any other NPC.
    for (const c of civilians) {
      const ev = updateCivilian(c, map, isOccupied, loop.dt, {
        player,
        guards,
        emit: (x, y, loud) => { sounds.emit(x, y, loud, c.id); },
      });
      if (ev?.kind === 'screamed') {
        everSeen = true; // a worker with your face and a voice counts as seen
        eventLog.add('A worker sees you and screams!', 'warn');
      }
      else if (ev?.kind === 'reported') eventLog.add('A worker reached a guard — they know where you were.', 'warn');
    }

    // -- Bullet flight + impact resolution --
    updateBullets(bullets, map, loop.dt, (ctx: ImpactContext) => {
      const { bullet, kind } = ctx;
      if (!bullet.plan) return;
      const { targetId, limb, damage } = bullet.plan;
      const fromPlayer = bullet.sourceId === player.id;
      // A shot LOCK sits on a wall/prop tile, so the bullet may stop as 'wall' at
      // the lock's tile rather than 'target'. Line-of-fire was checked at fire
      // time, so a stop AT the lock's tile is a hit — handle both kinds. Flat HP;
      // destroying it flips `locked` off, which its door/container reads.
      const lk = map.locks.find((l) => l.id === targetId);
      if (lk) {
        const atLock = Math.floor(bullet.x) === lk.x && Math.floor(bullet.y) === lk.y;
        if ((kind === 'target' || kind === 'wall') && atLock && lk.locked && !lk.body.dead) {
          const destroyed = damageLock(lk, damage);
          damageNumbers.spawn(lk.x, lk.y, damage, destroyed);
          if (destroyed) eventLog.add(`Lock ${lk.owner === 'door' ? 'on the door' : 'on the cabinet'} destroyed.`, 'combat');
        }
        return;
      }
      // Everything below is a DIRECT hit on a humanoid.
      if (kind !== 'target') return;
      // Resolve victim — either the player or a guard by id.
      if (targetId === player.id) {
        if (player.body.dead) return;
        if (!limb) return;
        if (player.godmode) return; // debug: bullets pass through you
        const sev = applyDamage(player.body, limb, damage);
        triggerHitAnim(player);
        damageVignette.pulse(0.45 + Math.min(0.55, damage / 25));
        bloodMap.add(player.x, player.y, bloodFromSeverity(sev));
        damageNumbers.spawn(player.x, player.y, damage, sev === 'kill');
        if (sev === 'kill') eventLog.add(`Guard #${bullet.sourceId} — takedown.`, 'warn');
        else eventLog.add(`Guard #${bullet.sourceId} hit your ${limb} (${sev}).`, 'warn');
        // Don't force focus — raise a directional "F Focus!" prompt so you can
        // CHOOSE to lock on and fight back, or ignore it and run.
        const attacker = guards.find((gg) => gg.id === bullet.sourceId);
        if (attacker) noteIncomingAttack(player, attacker, world.ticks);
        return;
      }
      const g = targets.find((gg) => gg.id === targetId);
      if (!g || g.body.dead) return;
      // A janitor is a janitor, not "Guard #N".
      const name = g.isJanitor ? 'the janitor' : `Guard #${targetId}`;
      if (!limb) {
        if (fromPlayer) eventLog.add(`Missed ${name}.`, 'combat');
        return;
      }
      const sev = applyDamage(g.body, limb, damage);
      triggerHitAnim(g);
      bloodMap.add(g.x, g.y, bloodFromSeverity(sev));
      damageNumbers.spawn(g.x, g.y, damage, sev === 'kill');
      // Attacked by the player → engage (even a silent hit blows your cover). A
      // janitor doesn't fight — it flees to warn a guard (handled in its update).
      if (fromPlayer && !g.body.dead && !g.isJanitor) engageGuard(g, player);
      if (sev === 'kill') {
        eventLog.add(`${name[0]!.toUpperCase()}${name.slice(1)} down.`, 'combat');
      } else if (fromPlayer) {
        eventLog.add(`Hit ${limb} on ${name} (${sev}).`, 'combat');
      }
    });
    // Compact the array — drop expired bullets.
    for (let i = bullets.length - 1; i >= 0; i--) if (!bullets[i]!.alive) bullets.splice(i, 1);

    sounds.tickFade(CONFIG.sound.fadeAgeTicks);
    tickDynamicLights(map, loop.dt); // age + drop expired muzzle-flash lights

    // -- Animations + blood + bleed --
    // A dead player is a floating ghost, not a downed body: pass a null body so
    // the tilt eases back to upright (it "rises" as a spirit) instead of lying on
    // its side. The corpse it left behind is drawn separately at `deathTile`.
    advanceEntityAnimation(player, player.body.dead ? null : player.body, loop.dt);
    // Bleeding drip + footprints on the player's current tile — only while ALIVE.
    // The death ghost free-flies with the same x/y, so a dead player must neither
    // drip fresh blood nor track footprints as it roams.
    if (!player.body.dead) {
      if (player.godmode) player.body.bleedPerSec = 0; // debug: no bleeding out either
      const playerBleed = bleedTickAmount(player.body, loop.dt);
      if (playerBleed > 0) bloodMap.add(player.x, player.y, playerBleed);
      // Detect fresh tile-entry for blood-trail pickup/drop.
      const pLast = lastTile.get(player.id);
      if (!pLast || pLast.x !== player.x || pLast.y !== player.y) {
        stepIntoTile(player, bloodMap, player.x, player.y, map);
        lastTile.set(player.id, { x: player.x, y: player.y });
      }
    }
    for (const g of guards) {
      advanceEntityAnimation(g, g.body, loop.dt);
      const gBleed = bleedTickAmount(g.body, loop.dt);
      if (gBleed > 0) bloodMap.add(g.x, g.y, gBleed);
      const gLast = lastTile.get(g.id);
      if (!gLast || gLast.x !== g.x || gLast.y !== g.y) {
        // A guard actively tracking a trail must not pick up + re-drop blood as
        // it walks — that would re-stamp the trail it's following as freshest
        // and make it chase its own footprints. Just record the tile.
        if (!g.bloodTrack) stepIntoTile(g, bloodMap, g.x, g.y, map);
        lastTile.set(g.id, { x: g.x, y: g.y });
      }
    }
    bloodMap.tickDry(loop.dt);

    if (consumeDebugToggle(input)) {
      renderer.perception.toggle();
      perceptionVisible = !perceptionVisible;
      // LOG + DEBUG + the MOVE dev panel are all debug tooling — show/hide them
      // together with the overlay.
      logWin.root.style.display = perceptionVisible ? '' : 'none';
      debugWin.root.style.display = perceptionVisible ? '' : 'none';
      moveWin.root.style.display = perceptionVisible ? '' : 'none';
      // Reveal/hide the lock window's dev randomizer with the overlay.
      if (pickSession) lockWindow.update(pickSession, perceptionVisible);
    }
  };

  // Prime the smoothed camera target on first frame so it doesn't lerp
  // from (0, 0) at map load.
  camera.targetX = player.visualX;
  camera.targetY = player.visualY;
  // Aim-up camera: the world rotates so the player's facing points screen-up.
  // Facing is set by the arrow keys, so the camera swings around the player to
  // whatever direction they aim. Prime it to the initial facing.
  camera.headingCurrent = headingForFacing(player.facingDx, player.facingDy);
  camera.heading = camera.headingCurrent;

  // Slowly-advancing phase that rotates the sound-field rainbow over time
  // for the iridescent shimmer. Wrapped to [0,1) to stay precise. ~0.08
  // cycles/sec = one full hue sweep every ~12s (subtle, not disco).
  let soundShimmer = 0;
  const SOUND_SHIMMER_RATE = 0.08;

  app.ticker.add((ticker) => {
    const dtSec = Math.min(ticker.deltaMS / 1000, 0.25);
    const { alpha, ticksRan } = loop.step(dtSec, tick);
    soundShimmer = (soundShimmer + dtSec * SOUND_SHIMMER_RATE) % 1;
    // Decay the live noise meter toward silence between emissions.
    playerNoise = Math.max(0, playerNoise - dtSec * NOISE_DECAY_PER_SEC);
    const px = lerp(player.prevVisualX, player.visualX, alpha);
    const py = lerp(player.prevVisualY, player.visualY, alpha);
    // Aim-up camera — the world rotates around the player:
    //   - Normal: pivot on the player; world rotates so the player's FACING
    //     (arrow-key aim) points screen-north.
    //   - Focus lock: pivot on the MIDPOINT between player and focused entity
    //     so both stay on screen; world rotates so the enemy→player vector
    //     points screen-down (enemy stays up).
    // Entity glyphs additionally rotate to their own facing, so guards' looking
    // direction reads correctly within the rotated frame.
    // NPCs the renderer draws / the player can focus: guards AND the janitor
    // (kept out of the guard-AI passes but rendered + focusable like an entity).
    // Stashed bodies are shut inside a prop — they don't render at all.
    const visibleNpcs = guards.filter((g) => !g.stashed);
    const onFloor = escortee && building && escortee.floorIdx === building.activeFloorIdx
      ? [escortee]
      : [];
    const renderNpcs: Guard[] = janitors.length || civilians.length || onFloor.length
      ? [...visibleNpcs, ...janitors, ...civilians, ...onFloor]
      : visibleNpcs;
    let desiredX = px;
    let desiredY = py;
    let desiredHeading = headingForFacing(player.facingDx, player.facingDy);
    const focused = player.focusedTargetId !== null
      ? renderNpcs.find((g) => g.id === player.focusedTargetId && !g.body.dead)
      : null;
    // Target zoom: normally the user's manual zoom. When locked on an enemy,
    // auto-zoom OUT (never in past the user's zoom) so the player↔enemy span
    // sits comfortably within the viewport.
    let targetZoom = userZoom;
    if (focused) {
      const fx = lerp(focused.prevVisualX, focused.visualX, alpha);
      const fy = lerp(focused.prevVisualY, focused.visualY, alpha);
      desiredX = (px + fx) / 2;
      desiredY = (py + fy) / 2;
      desiredHeading = headingForFocus(px - fx, py - fy);
      // Fit the full player→enemy span into ~55% of the smaller viewport
      // dimension (leaving a comfortable margin around both).
      const spanPx = Math.hypot(px - fx, py - fy) * renderer.tileSize;
      const viewport = Math.min(app.renderer.width, app.renderer.height);
      const fitZoom = spanPx > 1 ? (viewport * 0.55) / spanPx : maxZoom;
      targetZoom = Math.max(minZoom, Math.min(userZoom, fitZoom));
    }
    // Smoothly ease the actual zoom toward the target so focus-in/out glides.
    camera.zoom += (targetZoom - camera.zoom) * Math.min(1, dtSec * 8);
    updateCameraSmoothed(
      camera,
      desiredX,
      desiredY,
      1, // pivot lerp ignored (snapped) — player is dead-center
      renderer.tileSize,
      app.renderer.width,
      app.renderer.height,
      desiredHeading,
      // Heading lerp rate per frame. 0.08 took ~0.6s to settle a 45° turn,
      // which read as laggy; 0.22 settles in ~0.15s — snappy but still
      // animated rather than an instant snap.
      0.22,
      0,
      0.5,
    );
    // Instant sound field — same flood the guards hear through, no wave
    // animation. Computed fresh each frame from the live event set.
    const soundField = computeSoundField(sounds.events, map, soundShimmer, player.x, player.y);
    // Guard vision cones as a tile-colouring field (blue multiply on the glyph,
    // like the sound field). In normal play it's clipped to the player's own FOV
    // (only the intersection of the two cones shows). With the debug overlay on
    // (showAll) that clip drops and every guard's cone is revealed floor-wide.
    const visionField = computeVisionField(renderNpcs, fov, map, renderer.perception.isVisible());
    renderer.draw({ world, camera, alpha, fov, player, guards: renderNpcs, map, soundField, visionField, dt: dtSec, bullets, nightVision: player.nightVisionOn });
    renderer.bloodLayer.update(bloodMap);
    renderer.perception.update(renderNpcs, sounds.events, map, floor);
    // Highlight the focused entity — ANY perceivable NPC (guards AND the
    // janitor), not just guards, so a locked janitor gets the reticle too.
    renderer.perception.setFocus(player.focusedTargetId, renderNpcs, camera.headingCurrent);
    const seenNow = guards.some((g) => !g.body.dead && g.seesPlayerNow);
    if (seenNow) everSeen = true;
    taskbar.update(player, guards.length, highestState(guards), input, { current: playerNoise, max: maxNoise }, seenNow, map);
    debug.update(dtSec, ticksRan, player, guards.length, floor, guards);
    movePanel.update(input, player, camera);
    minimap.update(map, fov, player, guards);
    questLog.update(questline, (fi) => floorLabel(building?.floors[fi]?.floorNum ?? 1));
    // Surroundings view is only shown in focus mode (when the camera locks onto
    // an enemy and can push the player off-centre).
    surroundWin.root.style.display = player.focusActive ? '' : 'none';
    if (player.focusActive) surroundView.update(map, fov, player, renderNpcs);
    compass.update(player, guards);
    sidePanel.update(player, guards);
    if (inventoryWindow.isOpen()) inventoryWindow.update(player);
    // The FOCUS window shows ONLY while actually locked on a target — the panel
    // owns its own visibility from the focused-entity lookup (over guards AND the
    // janitor), so primed-but-unlocked focus mode keeps it hidden.
    focusPanel.update(player, renderNpcs);
    // Corpse loot label: fixed 58px above the player's on-screen point (real
    // transform), so it holds its spot around the player at any zoom.
    const pInv = renderer.world.toGlobal(new Point((px + 0.5) * renderer.tileSize, (py + 0.5) * renderer.tileSize));
    inventoryOverlay.update(pInv.x, pInv.y - 58, player.x, player.y, guards);
    damageNumbers.update(dtSec, (tx, ty) =>
      renderer.world.toGlobal(new Point((tx + 0.5) * renderer.tileSize, (ty + 0.5) * renderer.tileSize)),
    );
    damageVignette.update(dtSec);
    healVignette.update(dtSec);
    focusVignette.setActive(player.focusActive);
    stanceDisplay.update(player, dtSec);
    // Contextual action prompts float just above the player. Use the renderer's
    // own world transform (rotation + zoom + pivot aware) to find the player's
    // on-screen point so the badges track the character through camera spin.
    const pScreen = renderer.world.toGlobal(
      new Point((px + 0.5) * renderer.tileSize, (py - 0.4) * renderer.tileSize),
    );
    const ventHere = building
      ? { onGrille: grilleAt(ventNetwork(building.services, building.activeFloorIdx), player.x, player.y) !== null }
      : undefined;
    actionPrompts.update(pScreen.x, pScreen.y, computeActionPrompts(player, renderNpcs, map, ventHere));
    // Directional "F Focus!" prompt: place it near the player in the SCREEN
    // direction of the last attacker (world hit-dir rotated by the camera spin).
    {
      const pCenter = renderer.world.toGlobal(
        new Point((px + 0.5) * renderer.tileSize, (py + 0.5) * renderer.tileSize),
      );
      const h = camera.headingCurrent;
      const cos = Math.cos(h);
      const sin = Math.sin(h);
      const sdx = player.hitDirX * cos - player.hitDirY * sin;
      const sdy = player.hitDirX * sin + player.hitDirY * cos;
      const alpha = Math.min(1, player.focusPromptTimer / 0.6); // quick fade-out at the end
      focusPrompt.update(pCenter.x, pCenter.y, sdx, sdy, player.focusActive ? 0 : alpha);
    }
    if (fuseboxPanel.isOpen()) fuseboxPanel.update(map.rooms);
    // Cursor probe (only while the ` debug overlay is on): shows EVERYTHING the
    // cursor reveals, stacked — over a named HUD element its name; over the game
    // map the tile's room + light level (all lines at once, no swapping).
    if (perceptionVisible) {
      const hovered = document.elementFromPoint(mouseClientX, mouseClientY) as HTMLElement | null;
      const named = hovered?.closest('[data-inspect]') as HTMLElement | null;
      const lines: string[] = [];
      if (named) {
        lines.push(`▸ ${named.dataset.inspect}`);
      } else {
        const lp = renderer.world.toLocal(new Point(mouseClientX, mouseClientY));
        const tx = Math.floor(lp.x / renderer.tileSize);
        const ty = Math.floor(lp.y / renderer.tileSize);
        if (tx >= 0 && ty >= 0 && tx < map.width && ty < map.height) {
          const room = roomAt(map, tx, ty);
          lines.push(`▣ ${room ? room.name : 'outside'}`);
          const lvl = lightLevelAt(map, tx, ty);
          const nv = player.nightVisionOn ? ` → ${perceivedLight(map, tx, ty, true).toFixed(1)} NV` : '';
          lines.push(`☼ ${lvl.toFixed(1)}/10${nv}`);
        }
      }
      if (lines.length > 0) {
        lightProbe.textContent = lines.join('\n');
        lightProbe.style.display = 'block';
        lightProbe.style.left = `${mouseClientX + 16}px`;
        lightProbe.style.top = `${mouseClientY + 16}px`;
      } else {
        lightProbe.style.display = 'none';
      }
    } else if (lightProbe.style.display !== 'none') {
      lightProbe.style.display = 'none';
    }
  });

  // NEW GAME (procedural mission): deploy a generated building and drop into its
  // ground floor. `?gen[=seed]` is a dev shortcut that synthesizes a mission.
  {
    const genSeed = new URLSearchParams(location.search).get('gen');
    const cfg: MissionParams | null = mission
      ?? (genSeed !== null ? { budget: 3, type: 'random', timeOfDay: 'in-use', seed: Number(genSeed) || 1 } : null);
    if (cfg) {
      const est = rollEstablishment(cfg.seed, { budget: cfg.budget as Budget });
      missionOperating = cfg.timeOfDay;
      // 'random' picks a family from the seed so a rolled mission is still
      // reproducible from its seed alone. Resolved BEFORE generation, because
      // the type decides which room the building must contain.
      const families: MissionType[] = ['assassination', 'heist', 'data-theft', 'recon', 'combined'];
      const type: MissionType = cfg.type === 'random'
        ? families[Math.floor(mulberry32(hashSeed(cfg.seed, 0x717))() * families.length)]!
        : (cfg.type as MissionType);
      building = generateBuilding(est, { operating: cfg.timeOfDay, missionType: type });
      questline = deriveQuestline(building, type, cfg.seed);
      questWin.root.style.display = '';
      const g = building.floors[building.activeFloorIdx]!;
      eventLog.add(`Deployed: ${est.name} · $${est.budget} · ${building.floors.length} floors`, 'system');
      for (const t of visibleTasks(questline)) {
        eventLog.add(`Objective: ${t.title} · ${floorLabel(building.floors[t.floorIdx]!.floorNum)}`, 'stealth');
      }
      switchToFloor(building.activeFloorIdx, g.map.spawn);
    }
  }

  (globalThis as unknown as { chagat: unknown }).chagat = {
    world, player, guards, map, input, camera, renderer, loop, debug, sounds, fov, eventLog, bloodMap,
    spawnGuard: (x: number, y: number) => guards.push(spawnGuard(world, x, y)),
    respawn: () => {
      respawnPlayer(player, map.spawn.x, map.spawn.y);
      fov.reset();
      bloodMap.reset();
      for (const g of guards) { g.bloodSeen.clear(); g.bloodSeenTick = 0; }
    },
    highestState: () => highestState(guards),
    lastAttack: () => lastAttack,
    // Multi-floor debug hooks (map/guards are reassigned per floor, so read live).
    getMap: () => map,
    getGuards: () => guards,
    building: () => building,
    activeFloorIdx: () => building?.activeFloorIdx ?? null,
    floorCount: () => building?.floors.length ?? 0,
    useStairs: (dir: 1 | -1) => useStairs(dir),
    goFloor: (idx: number) => { if (building) switchToFloor(idx, building.floors[idx]!.stairs.down); },
    tamper: (kind: ServiceKind) => {
      const s = building?.services.find((x) => x.kind === kind);
      if (s) tamperService(s);
    },
    services: () => building?.services.map((s) => ({
      kind: s.kind, floorIdx: s.floorIdx, plant: s.plant, riser: s.riser,
      outlets: s.outlets.length, state: s.state,
    })) ?? [],
  };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// Boot behind the title screen: START TUTORIAL runs main() on the hand-built
// floor; NEW GAME opens the mission screen, then boots main(mission) on a
// generated building. main() is one-way — a reload is the way back to the menu
// (no partial teardown of the Pixi app + window listeners).
//
// A deployed mission writes itself into the URL, which makes a reload REPLAY the
// same contract: that's what the end screen's REDEPLOY does, and it means a
// building you want to show someone is just a link.
export function missionToUrl(m: MissionParams): string {
  const p = new URLSearchParams({
    budget: String(m.budget), type: m.type, time: m.timeOfDay, seed: String(m.seed),
  });
  return `${location.pathname}?${p.toString()}`;
}

function missionFromUrl(): MissionParams | null {
  const p = new URLSearchParams(location.search);
  const seed = Number(p.get('seed'));
  if (!p.has('seed') || !Number.isFinite(seed)) return null;
  const time = p.get('time') === 'off-duty' ? 'off-duty' : 'in-use';
  return {
    budget: Number(p.get('budget')) || 3,
    type: p.get('type') ?? 'random',
    timeOfDay: time,
    seed,
  };
}

let booted = false;
const titleScreen = new TitleScreen({
  onStartTutorial: () => boot(),
  onNewGame: (mission) => boot(mission),
});
function boot(mission?: MissionParams): void {
  if (booted) return;
  booted = true;
  if (mission) history.replaceState(null, '', missionToUrl(mission));
  titleScreen.hide();
  void main(mission);
}

// A URL carrying a mission drops you straight into it — no menu round-trip.
const fromUrl = missionFromUrl();
if (fromUrl) boot(fromUrl);
else titleScreen.show();
